import { useState, useSyncExternalStore } from 'react';
import { Alert, Image, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import type { Anexo, Reg } from '../store/types';
import { ehFoto, fotoReduzida, sugerirFotos } from '../lib/fotosPdf';
import { fonteImagem } from '../components/Anexos';
import type { Colecao, Ctx, ModDef, PainelProps, TopoProps } from './tipos';
import type { Action } from '../components/ui';
import { dataBr, diasAte, diasEntre, din, dinDe, dinSomas, junta, moedaDo, num, op, somaPorMoeda, type Somas } from './util';
import { excluirReg, mudarCampos, novoReg, regsDe, salvarReg } from '../store/modulos';
import { abrirBusca, rotaPorTodos } from '../lib/mapa';
import { compartilharTexto } from '../lib/anexos';
import { semBloqueio } from '../auth/AuthContext';
import { Btn, Chip, Field, Sheet } from '../components/ui';
import { CartaoTotal } from '../components/relatorio/Periodo';
import { RelatoriosGerados } from '../components/RelatoriosGerados';
import { marcarAbaAtual } from '../lib/relatorios';
import { dinheiro, lerValor } from '../lib/moeda';
import { C, alpha } from '../components/theme';
import { fusoAparelho, horaEm, nomeFuso, rotuloGMT } from '../lib/fuso';
import { addDays, labelDia, norm, today, uid, horaTela } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { PainelAlbum } from '../components/Album';
import { Recolhivel, CartaoApoio } from '../components/Recolhivel';
import { membrosDe } from '../lib/grupos';
import { gruposEmCache } from '../lib/grupos';
import { gerarECompartilharPdf, type BlocoPdf } from '../lib/pdf';
import { compartilharXlsx } from '../lib/planilha';
import { enviarListaAoPlanner, listaNoPlanner } from '../lib/enviarPlanner';
import { getDoc } from '../store/store';
import { EditorOrcamento, ORC_PADRAO, ResumoOrcamento, gastoCat, gastoLinha, mesmo, orcDe, previstoCat, previstoTotal, somaGastos, type CatOrc, type LinhaOrc } from './viagensOrcamento';

const CLIMAS = [N_('Frio'), N_('Ameno'), N_('Quente'), N_('Praia'), N_('Neve')];
const TIPOS_RESERVA = [N_('Voo'), N_('Hotel'), N_('Aluguel de carro'), N_('Trem ou ônibus'), N_('Passeio'), N_('Seguro viagem'), N_('Restaurante'), N_('Outro')];
const ICONE_RESERVA: Record<string, string> = { 'Voo': '🛫', 'Hotel': '🏨', 'Aluguel de carro': '🚙', 'Trem ou ônibus': '🚆', 'Passeio': '🎟️', 'Seguro viagem': '🛡️', 'Restaurante': '🍽️' };
/** Texto 19 (G2d): categoria do orçamento em que a reserva paga entra nos gastos. */
const CAT_RESERVA: Record<string, string> = { 'Voo': 'Transporte', 'Hotel': 'Hospedagem', 'Aluguel de carro': 'Transporte', 'Trem ou ônibus': 'Transporte',
  'Passeio': 'Passeios', 'Restaurante': 'Alimentação' };

// ─── Pessoas da viagem (texto 19, B) ─────────────────────────────────────────────────────────────
// Quem vai = os membros do grupo da viagem (entram sozinhos) + os nomes avulsos escritos em "Quem vai".
const membrosCache = new Map<string, string[]>();
const pedindo = new Set<string>();
const ouvM = new Set<() => void>();
let versaoM = 0;
function membrosDoGrupo(g: string | null | undefined): string[] {
  if (!g) return [];
  const m = membrosCache.get(g);
  if (!m && !pedindo.has(g)) {
    pedindo.add(g);
    membrosDe(g).then(l => { membrosCache.set(g, l.map(x => String(x.nome ?? '').trim()).filter(Boolean)); versaoM++; ouvM.forEach(f => f()); })
      .catch(() => {}).finally(() => { setTimeout(() => pedindo.delete(g), 60000); });
  }
  return m ?? [];
}
/** Atualiza a tela quando os membros do grupo chegam do servidor. */
export const useMembrosViagem = () => useSyncExternalStore(cb => { ouvM.add(cb); return () => { ouvM.delete(cb); }; }, () => versaoM, () => versaoM);
/** Nomes escritos à mão em "Quem vai" ("Walter, Ana e Bia" vira ["Walter", "Ana", "Bia"]). */
export function avulsosDaViagem(v: Reg | null): string[] {
  const p = v?.c.pessoas;
  const l = Array.isArray(p) ? p.map(String) : String(p ?? '').split(/,|;|\/|\s+e\s+|\s+and\s+|\s+y\s+|&|\n/i);
  return [...new Set(l.map(x => x.trim()).filter(Boolean))].slice(0, 20);
}
/** Membros do grupo da viagem (só quando o grupo tem 2 pessoas ou mais conta como "viagem com outras pessoas"). */
export const membrosDaViagem = (v: Reg | null) => membrosDoGrupo(v?.g);
/** Quantas pessoas há no grupo desta ficha (null = sem grupo ou ainda não sabe). */
export function pessoasNoGrupo(g: string | null | undefined): number | null {
  if (!g) return null;
  const c = gruposEmCache().lista?.find(x => x.id === g);
  return c ? c.membros : membrosCache.get(g)?.length ?? null;
}

/** Situação da viagem: "Faltam 12 dias", "Dia 3 de 7" ou "Terminou". */
export function situacaoViagem(r: Reg, hoje = today()) {
  const ini = r.c.inicio as string, fim = (r.c.fim as string) || ini;
  if (!ini) return '';
  if (hoje < ini) { const n = diasAte(ini, hoje); return n === 1 ? t('Começa amanhã') : t('Faltam {n} dias', { n }); }
  if (hoje <= fim) return t('Dia {d} de {total}', { d: diasEntre(ini, hoje) + 1, total: diasEntre(ini, fim) + 1 });
  return t('Terminou');
}
export const viagemNoDia = (r: Reg, dia: string) => r.col === 'viagem' && !!r.c.inicio && r.c.inicio <= dia && dia <= (r.c.fim || r.c.inicio);

/** Lista de mala pelo clima, pelos dias e por quem vai. Os itens já saem no idioma do app. */
export function gerarMala(clima: string, dias: number, extras: string[]): string[] {
  const d = Math.max(1, Math.min(dias || 3, 30));
  const q = (n: number, item: string) => t('{n}× {item}', { n, item: t(item) });
  const l: string[] = [
    t(N_('Documentos (passaporte, identidade, carteira de motorista)')), t(N_('Cartões e dinheiro')), t(N_('Celular e carregador')),
    t(N_('Adaptador de tomada')), t(N_('Remédios de uso contínuo')), t(N_('Escova e pasta de dente')), t(N_('Desodorante')),
    t(N_('Itens de higiene (xampu, sabonete)')), t(N_('Fone de ouvido')),
    q(Math.min(d + 1, 10), N_('Roupas íntimas')), q(Math.min(d + 1, 10), N_('Pares de meia')), q(Math.min(d, 7), N_('Camisetas')),
    q(Math.max(1, Math.ceil(Math.min(d, 7) / 2)), N_('Calças ou bermudas')), q(d > 4 ? 2 : 1, N_('Pijamas')), t(N_('Sapato confortável')),
  ];
  if (clima === 'Frio' || clima === 'Neve') l.push(q(Math.min(3, Math.ceil(d / 2)), N_('Blusas de frio')), t(N_('Casaco pesado')), t(N_('Gorro, cachecol e luvas')), t(N_('Calça térmica (segunda pele)')), t(N_('Hidratante labial')));
  if (clima === 'Neve') l.push(t(N_('Jaqueta impermeável')), t(N_('Botas para neve')), t(N_('Óculos de sol')));
  if (clima === 'Ameno') l.push(t(N_('Casaco leve')), t(N_('Guarda-chuva')));
  if (clima === 'Quente' || clima === 'Praia') l.push(t(N_('Protetor solar')), t(N_('Chapéu ou boné')), t(N_('Óculos de sol')), t(N_('Repelente')), t(N_('Garrafa de água')));
  if (clima === 'Praia') l.push(q(2, N_('Roupas de banho')), t(N_('Toalha de praia')), t(N_('Chinelo')), t(N_('Saída de praia')));
  if (extras.includes('Crianças')) l.push(t(N_('Roupas extras das crianças')), t(N_('Lanches e água')), t(N_('Brinquedo ou livro para a viagem')), t(N_('Documentos e autorização das crianças')));
  if (extras.includes('Trabalho')) l.push(t(N_('Notebook e carregador')), t(N_('Roupa social')), t(N_('Cartões de visita')));
  if (extras.includes('Bebê')) l.push(t(N_('Fraldas e lenços')), t(N_('Mamadeiras e leite')), t(N_('Trocador portátil')));
  if (d > 7) l.push(t(N_('Sabão para lavar roupa')));
  l.push(t(N_('Kit de primeiros socorros')));
  return l;
}

/** Relógios (aqui e no destino) e a situação da viagem. */
function PainelViagem({ ctx, pai }: PainelProps) {
  if (!pai) return null;
  const tz = pai.c.fuso as string;
  const aqui = fusoAparelho();
  const s = situacaoViagem(pai);
  return (
    <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
      {!!s && <View style={{ backgroundColor: alpha(ctx.cor, 0.18), borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8 }}><Text style={{ color: C.text, fontWeight: '800' }}>✈️ {s}</Text></View>}
      <View style={{ backgroundColor: C.card, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8 }}>
        <Text style={{ color: C.sub, fontSize: 11 }}>🏠 {t('Aqui')} · {rotuloGMT(aqui)}</Text>
        <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{horaEm(aqui)}</Text>
      </View>
      {!!tz && tz !== aqui && (
        <View style={{ backgroundColor: C.card, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8 }}>
          <Text style={{ color: C.sub, fontSize: 11 }}>📍 {nomeFuso(tz)}</Text>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>{horaEm(tz)}</Text>
        </View>
      )}
    </View>
  );
}

// ─── Câmbio (0.60) ───────────────────────────────────────────────────────────────────────────────
const MOEDAS_VIAGEM = ['USD', 'EUR', 'GBP', 'ARS', 'CLP', 'UYU', 'PYG', 'BOB', 'PEN', 'COP', 'MXN', 'CAD', 'CHF', 'JPY', 'CNY', 'AUD', 'ZAR', 'AED', 'TRY', 'THB', 'BRL'];
/** A viagem tem moeda diferente da do aparelho? Então devolve a moeda e o câmbio (0 = ainda sem câmbio). */
export function cambioDa(v: Reg | null | undefined, ctx: Ctx) {
  const m = String(v?.c.moedaDestino || '');
  if (!m || m === ctx.moeda) return null;
  return { moeda: m, taxa: num(v!.c.cambio) };
}
/** Câmbio do dia pela tabela do Banco Central Europeu (Frankfurter, grátis e sem cadastro). */
async function buscarCambio(de: string, para: string): Promise<number> {
  const r = await fetch(`https://api.frankfurter.dev/v1/latest?base=${encodeURIComponent(de)}&symbols=${encodeURIComponent(para)}`);
  if (!r.ok) throw new Error(String(r.status));
  const j: any = await r.json();
  const v = Number(j?.rates?.[para]);
  if (!isFinite(v) || v <= 0) throw new Error('sem taxa');
  return v;
}

/** Câmbio da viagem: a taxa, o botão de buscar a de hoje e uma calculadora rápida. */
function PainelCambio({ ctx, pai, pode }: { ctx: Ctx; pai: Reg; pode: boolean }) {
  const cb = cambioDa(pai, ctx);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState('');
  if (!cb) return null;
  const atualizar = async () => {
    setBusy(true);
    try {
      const taxa = await buscarCambio(cb.moeda, ctx.moeda);
      mudarCampos(ctx.mod, pai.id, { cambio: Math.round(taxa * 10000) / 10000, cambioEm: today() });
    } catch {
      Alert.alert(t('Não foi possível buscar o câmbio'), t('Sem internet ou a moeda {m} não está na tabela. Edite a viagem e coloque o câmbio à mão.', { m: cb.moeda }));
    } finally { setBusy(false); }
  };
  const lido = lerValor(v);
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>💱 {cb.taxa ? `${dinheiro(1, cb.moeda)} = ${dinheiro(cb.taxa, ctx.moeda)}` : t('Câmbio de {m} ainda não definido', { m: cb.moeda })}</Text>
        {pode && <Pressable disabled={busy} onPress={atualizar} hitSlop={6}><Text style={{ color: ctx.cor, fontWeight: '800', fontSize: 12 }}>{busy ? '…' : '↻ ' + t('Câmbio de hoje')}</Text></Pressable>}
      </View>
      {!!pai.c.cambioEm && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Atualizado em {d}', { d: dataBr(pai.c.cambioEm) })}</Text>}
      {cb.taxa > 0 && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Field value={v} onChangeText={setV} placeholder={t('Valor em {m}', { m: cb.moeda })} keyboardType="decimal-pad" style={{ flex: 1, minWidth: 0, paddingVertical: 8 }} />
          <Text style={{ color: C.text, fontWeight: '800', minWidth: 110, textAlign: 'right' }}>{isFinite(lido) && lido > 0 ? '= ' + dinheiro(lido * cb.taxa, ctx.moeda) : ''}</Text>
        </View>
      )}
    </View>
  );
}

const Dica = ({ texto }: { texto: string }) => <Text style={{ color: C.sub, fontSize: 12 }}>💡 {texto}</Text>;

/** Roteiro (0.59): relógios, situação e a dica de que os lugares com dia entram sozinhos aqui. */
function PainelRoteiro(p: PainelProps) {
  return (
    <View style={{ gap: 8 }}>
      <PainelViagem {...p} />
      {!!p.pai && <PainelCambio ctx={p.ctx} pai={p.pai} pode={p.pode !== false} />}
      <Dica texto={t('Esta tela mostra a linha do tempo da viagem, com as atividades e os lugares que têm dia marcado. Para adicionar ou editar as informações completas de um lugar, use a aba Lugares.')} />
    </View>
  );
}

/** Dia do roteiro: atividade (data) ou lugar com dia marcado (dia). */
const diaDe = (r: Reg) => String(r.c.data || r.c.dia || '9999');
/** Lugar que já tem uma atividade igual no roteiro (criada antes da 0.59 pelo "Pôr no roteiro"): não aparece duas vezes. */
const jaNoRoteiro = (ctx: Ctx, l: Reg) => ctx.filhos(l.pai!, 'roteiro').some(x => x.c.data === l.c.dia && norm(String(x.c.atividade ?? '')).trim() === norm(String(l.c.nome ?? '')).trim());

/** Copia a atividade ou o lugar para outra viagem (o dia vai para dentro das datas dela). */
function paraOutraViagem(ctx: Ctx, r: Reg): Action[] {
  const outras = ctx.regs.filter(v => v.col === 'viagem' && v.id !== r.pai)
    .sort((a, b) => String(b.c.inicio || '').localeCompare(String(a.c.inicio || ''))).slice(0, 6);
  return outras.map(v => ({ label: '✈️ ' + t('Para a viagem {d}', { d: v.c.destino || t('sem nome') }), onPress: () => {
    const k = r.col === 'lugar' ? 'dia' : 'data';
    const dia = String(r.c[k] || '');
    const dentro = dia && v.c.inicio && dia >= v.c.inicio && dia <= (v.c.fim || v.c.inicio);
    salvarReg(ctx.mod, { ...novoReg(r.col, v.id, { ...r.c, [k]: dentro ? dia : r.col === 'lugar' ? '' : v.c.inicio || dia, fuso: v.c.fuso || r.c.fuso || '' }),
      anexos: r.anexos?.filter(a => a.url || a.nuvem), itens: r.itens?.map(i => ({ ...i, id: uid(), done: false })) });
    Alert.alert(t('Enviado'), t('Foi para a viagem {d}.', { d: v.c.destino || '' }));
  } }));
}

/** Gera a lista da mala pelo clima e pelos dias da viagem. */
function PainelMala({ ctx, pai }: PainelProps) {
  const [clima, setClima] = useState<string>(pai?.c.clima || 'Ameno');
  const [extras, setExtras] = useState<string[]>([]);
  if (!pai) return null;
  const dias = pai.c.inicio ? diasEntre(pai.c.inicio, pai.c.fim || pai.c.inicio) + 1 : 3;
  function gerar() {
    const r = novoReg('mala', pai!.id, { nome: t('Mala ({clima}, {n} dias)', { clima: t(clima), n: dias }) });
    r.itens = gerarMala(clima, dias, extras).map(text => ({ id: uid(), text, done: false }));
    salvarReg(ctx.mod, r);
  }
  return (
    <CartaoApoio titulo={'🧳 ' + t('Montar a mala pelo clima ({n} dias)', { n: dias })} chave={'viagens:mala'} gap={8}>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        {CLIMAS.map(c => <Chip key={c} small cor={ctx.cor} on={clima === c} label={t(c)} onPress={() => setClima(c)} />)}
      </View>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        {[N_('Crianças'), N_('Bebê'), N_('Trabalho')].map(x => <Chip key={x} small cor={ctx.cor} on={extras.includes(x)} label={'+ ' + t(x)} onPress={() => setExtras(l => (l.includes(x) ? l.filter(y => y !== x) : [...l, x]))} />)}
      </View>
      <Pressable onPress={gerar} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}><Text style={{ color: '#fff', fontWeight: '800' }}>{t('Gerar lista da mala')}</Text></Pressable>
    </CartaoApoio>
  );
}

const confirmada = (r: Reg) => !!(r.c.confirmada || r.c.pago);
/** Texto 19 (G2d): reserva paga entra em Gastos já como paga (sem digitar de novo); desligou o "pago", o gasto sai. */
function gastoDaReserva(ctx: Ctx, r: Reg) {
  if (!r.pai) return;
  const regs = regsDe(ctx.mod);
  const ja = regs.find(g => g.col === 'gasto' && g.c.reserva === r.id);
  const valor = num(r.c.valorPago) || num(r.c.valor);
  if (r.c.pago && valor > 0) {
    const moeda = r.c.moedas?.valorPago ?? r.c.moedas?.valor;
    const data = String(r.c.pagoEm || '') || today();
    if (ja) { if (num(ja.c.valor) !== valor || ja.c.data !== data) mudarCampos(ctx.mod, ja.id, { valor, data, ...(moeda ? { moedas: { ...(ja.c.moedas ?? {}), valor: moeda } } : {}) }); return; }
    const v = regs.find(x => x.id === r.pai) ?? null;
    salvarReg(ctx.mod, novoReg('gasto', r.pai, { desc: junta(op(r.c.tipo), r.c.nome) || t('Reserva'), valor, data, cat: CAT_RESERVA[r.c.tipo] ?? 'Outro', item: '',
      pagou: pessoasDaViagem(v)[0] ?? '', pago: true, reserva: r.id, parada: paradaNoDia(ctx, r.pai, data)?.id ?? '', ...(moeda ? { moedas: { valor: moeda } } : {}) }));
  } else if (!r.c.pago && ja) excluirReg(ctx.mod, ja.id);
}

/** Texto 19 (A5/B): no alto da viagem, o "Resumo da viagem" com "O que ainda falta" (tocar leva à sub-aba no filtro "Falta"). */
function TopoViagem({ r, ctx, irPara }: TopoProps) {
  useMembrosViagem();
  const f = (col: string) => ctx.filhos(r.id, col);
  const lug = f('lugar').filter(x => !x.c.visitado).length;
  const res = f('reserva').filter(x => !confirmada(x)).length;
  const mala = f('mala').flatMap(x => x.itens ?? []).filter(i => !i.done).length;
  const rot = f('roteiro').filter(x => !x.c.feito).length;
  const gastos = f('gasto');
  const paradas = f('parada');
  const previsto = previstoTotal(orcDe(r)) + paradas.reduce((s, x) => s + previstoTotal(orcDe(x)), 0) || num(r.c.orcamento);
  const resta = previsto - somaGastos(gastos, ctx.moeda);
  const linhas: [string, string, string | null][] = [];
  if (rot) linhas.push(['🗺️ ' + (rot === 1 ? t('1 atividade do roteiro por fazer') : t('{n} atividades do roteiro por fazer', { n: rot })), 'roteiro', 'Falta']);
  if (lug) linhas.push(['📍 ' + (lug === 1 ? t('1 lugar não visitado') : t('{n} lugares não visitados', { n: lug })), 'lugar', 'Faltam']);
  if (res) linhas.push(['🎫 ' + (res === 1 ? t('1 reserva por confirmar') : t('{n} reservas por confirmar', { n: res })), 'reserva', 'Falta confirmar']);
  if (mala) linhas.push(['🧳 ' + (mala === 1 ? t('1 item por pôr na mala') : t('{n} itens por pôr na mala', { n: mala })), 'mala', 'Falta pôr']);
  if (previsto > 0) linhas.push(['💰 ' + (resta >= 0 ? t('Ainda pode gastar {v}', { v: din(resta, ctx.moeda) }) : t('Passou do previsto em {v}', { v: din(-resta, ctx.moeda) })), 'gasto', null]);
  if (!linhas.length) return <RelatorioViagem ctx={ctx} />;
  return (<>
    <RelatorioViagem ctx={ctx} />
    <Recolhivel visual="cartao" inicial chave={`aba:${ctx.mod}:resumoViagem`} titulo={'📋 ' + t('Resumo da viagem')}>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800', letterSpacing: 0.4 }}>{t('O QUE AINDA FALTA')}</Text>
      {linhas.map(([txt, col, filtro]) => (
        <Pressable key={col} onPress={() => irPara?.(col, filtro)} disabled={!irPara} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 }}>
          <Text style={{ color: C.text, flex: 1 }}>{txt}</Text>
          {!!irPara && <Text style={{ color: C.mute }}>›</Text>}
        </Pressable>
      ))}
    </Recolhivel>
  </>);
}

const viagem: Colecao = {
  id: 'viagem', nome: N_('Viagens'), um: N_('Viagem'), icone: '✈️',
  campos: [
    { k: 'destino', r: N_('Destino'), t: 'texto', ph: N_('Ex.: Lisboa, Portugal') },
    { k: 'inicio', r: N_('Ida'), t: 'data', aviso: [7, 1] },
    { k: 'fim', r: N_('Volta'), t: 'data', alerta: true, semHoje: true },
    { k: 'fuso', r: N_('Fuso horário do destino'), t: 'fuso' },
    { k: 'clima', r: N_('Clima esperado'), t: 'opcao', op: CLIMAS },
    { k: 'pessoas', r: N_('Quem vai'), t: 'nomes', nomesAuto: (_ctx, reg) => membrosDaViagem(reg),
      dica: N_('Quem está no grupo da viagem entra sozinho. Use "+ Adicionar pessoa" para quem não tem o app.') },
    { k: 'orcamento', r: N_('Orçamento total (se não for detalhar)'), t: 'dinheiro' },
    { k: 'moedaDestino', r: N_('Moeda do destino'), t: 'opcao', op: MOEDAS_VIAGEM },
    { k: 'cambio', r: N_('Câmbio: quanto vale 1 da moeda do destino na sua moeda'), t: 'numero', mostrar: c => !!c.moedaDestino },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.destino || '',
  sub: r => junta(r.c.inicio && `${dataBr(r.c.inicio)}${r.c.fim ? ' – ' + dataBr(r.c.fim) : ''}`, situacaoViagem(r)),
  ordem: (a, b) => {
    const h = today(), fa = (a.c.fim || a.c.inicio || '') < h, fb = (b.c.fim || b.c.inicio || '') < h;
    return fa !== fb ? (fa ? 1 : -1) : fa ? String(b.c.inicio).localeCompare(String(a.c.inicio)) : String(a.c.inicio || '9').localeCompare(String(b.c.inicio || '9'));
  },
  feito: r => !!(r.c.fim || r.c.inicio) && (r.c.fim || r.c.inicio) < today(),
  anexos: true, segExtra: { orc: 'fin' },
  // 0.61: relatório da viagem inteira (para guardar, mandar para quem foi ou prestar contas)
  acoes: (r, ctx) => [
    { label: '📄 ' + t('Relatório da viagem (PDF)'), onPress: () => abrirRelatorioViagem(r.id) },
    { label: '📊 ' + t('Planilha da viagem (Excel)'), onPress: () => { semBloqueio(() => relatorioViagemXlsx(ctx, r)).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); } },
  ],
  novo: () => ({ fuso: '' }),
  // texto 19 (F2): a viagem também vai para o My Planner pelo ⋮ (de um dia ao outro), só quando a pessoa manda
  agenda: r => (r.c.inicio ? { start: r.c.inicio, end: r.c.fim && r.c.fim > r.c.inicio ? r.c.fim : null } : null),
  semEnviarLista: true,
  painelTopo: TopoViagem,
  topoLista: ({ ctx }) => <RelatorioViagem ctx={ctx} />,   // a janela do relatório, para o ⋮ da viagem na lista
  vazio: N_('Cadastre a viagem e, dentro dela, o roteiro de cada dia, as reservas e a mala. Durante a viagem, o roteiro aparece no My Planner.'),
};

const roteiro: Colecao = {
  id: 'roteiro', pai: 'viagem', nome: N_('Roteiro'), um: N_('Atividade do roteiro'), icone: '🗺️',
  campos: [
    { k: 'atividade', r: N_('O que fazer'), t: 'texto', ph: N_('Ex.: Visita ao Mosteiro dos Jerónimos') },
    { k: 'data', r: N_('Dia'), t: 'data' },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'fuso', r: N_('Fuso horário'), t: 'fuso' },
    { k: 'local', r: N_('Local ou endereço'), t: 'endereco' },
    { k: 'valor', r: N_('Preço ou ingresso'), t: 'dinheiro' },
    { k: 'link', r: N_('Site ou ingresso online'), t: 'link' },
    { k: 'feito', r: N_('Concluído'), t: 'sim', interruptor: true, dataAoLigar: 'feitoEm', rotuloLigado: N_('Concluído em {d}') },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.atividade || '',
  sub: (r, ctx) => junta(horaTela(r.c.hora), r.c.local, num(r.c.valor) ? din(r.c.valor, ctx.moeda) : ''),
  data: 'data', ordem: (a, b) => `${diaDe(a)}${a.c.hora || '99'}`.localeCompare(`${diaDe(b)}${b.c.hora || '99'}`),
  feito: r => !!r.c.feito, check: 'feito',
  filtros: [[N_('Falta'), r => !(r.c.feito || r.c.visitado)], [N_('Feito'), r => !!(r.c.feito || r.c.visitado)]], filtroArquivados: true, semEnviarLista: true,
  progresso: l => ({ a: l.filter(r => !!r.c.feito).length, b: l.length, rotulo: N_('{a} de {b} feitos') }),
  // 0.59: os lugares com dia marcado aparecem sozinhos no roteiro, na ordem do dia e do horário
  incluir: (ctx, pai) => (pai ? ctx.filhos(pai.id, 'lugar').filter(l => !!l.c.dia && !jaNoRoteiro(ctx, l)) : []),
  enviarPara: (r, ctx) => paraOutraViagem(ctx, r),
  // 0.60: um bloco por dia, com o nome do dia, o dia da viagem e o custo previsto do dia
  grupoDe: r => diaDe(r),
  rotuloGrupo: (k, itens, ctx, pai) => {
    if (k === '9999') return t('Sem dia marcado');
    const ini = pai?.c.inicio, fim = pai?.c.fim || ini;
    const n = ini && k >= ini && k <= fim ? t('Dia {d} de {total}', { d: diasEntre(ini, k) + 1, total: diasEntre(ini, fim) + 1 }) : '';
    const custo = itens.reduce((s, x) => s + num(x.c.valor ?? x.c.preco), 0);
    return junta(labelDia(k), n, custo ? din(custo, ctx.moeda) : '');
  },
  // arrastar um lugar para excluir no roteiro: pode só tirar o dia (ele continua em Lugares)
  tirarIncluido: (r, ctx) => mudarCampos(ctx.mod, r.id, { dia: '', hora: '' }),
  anexos: true,
  novo: (_, pai) => ({ data: pai?.c.inicio && pai.c.inicio > today() ? pai.c.inicio : today(), fuso: pai?.c.fuso || '' }),
  agenda: r => ({ time: r.c.hora || null, tz: r.c.hora ? r.c.fuso || null : null, notes: r.c.local ? t('Local: {l}', { l: r.c.local }) : '' }),
  painel: PainelRoteiro,
  vazio: N_('Monte o roteiro dia a dia. Os lugares com dia marcado entram aqui sozinhos. No período da viagem, o roteiro aparece na linha do tempo do My Planner.'),
};

const reserva: Colecao = {
  id: 'reserva', check: 'pago', pagamento: { tipo: 'pagar', venc: 'data', data: 'pagoEm', valorPago: 'valorPago' }, pai: 'viagem', nome: N_('Reservas'), um: N_('Reserva'), icone: r => ICONE_RESERVA[r.c.tipo] ?? '🎫',
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS_RESERVA, outro: true },
    { k: 'nome', r: N_('Empresa, hotel ou voo'), t: 'texto', ph: N_('Ex.: TAP TP 108') },
    { k: 'codigo', r: N_('Código da reserva (localizador)'), t: 'texto' },
    { k: 'confirmada', r: N_('Reserva confirmada'), t: 'sim', interruptor: true, dataAoLigar: 'confirmadaEm', rotuloLigado: N_('Confirmada em {d}') },
    // texto 19 (G2f): só no voo, de onde sai, para onde vai e a hora de chegada (opcionais)
    { k: 'origem', r: N_('Origem'), t: 'texto', ph: N_('Ex.: São Paulo (GRU)'), mostrar: c => c.tipo === 'Voo' },
    { k: 'destinoVoo', r: N_('Destino'), t: 'texto', ph: N_('Ex.: Lisboa (LIS)'), mostrar: c => c.tipo === 'Voo' },
    { k: 'data', r: N_('Data (embarque ou check-in)'), t: 'data', aviso: [1], semHoje: true },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'chegada', r: N_('Horário de chegada'), t: 'hora', mostrar: c => c.tipo === 'Voo' },
    { k: 'fuso', r: N_('Fuso horário'), t: 'fuso' },
    { k: 'fim', r: N_('Volta ou check-out'), t: 'data', semHoje: true },
    { k: 'endereco', r: N_('Endereço ou terminal'), t: 'endereco' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'link', r: N_('Link da reserva'), t: 'link' },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'pago', r: N_('Já está pago'), t: 'sim', seg: 'fin', interruptor: true, dica: N_('Ao ligar, o valor entra em Gastos como pago.') },
    { k: 'pagoEm', r: N_('Data do pagamento'), t: 'data', mostrar: c => !!c.pago, seg: 'fin' },
    { k: 'valorPago', r: N_('Valor pago'), t: 'dinheiro', mostrar: c => !!c.pago },
  ],
  titulo: r => junta(op(r.c.tipo), r.c.nome),
  sub: (r, ctx) => junta(r.c.tipo === 'Voo' && (r.c.origem || r.c.destinoVoo) ? `${r.c.origem || '?'} → ${r.c.destinoVoo || '?'}` : '', r.c.codigo && `#${r.c.codigo}`,
    horaTela(r.c.hora) + (r.c.chegada ? ' → ' + horaTela(r.c.chegada) : ''), r.c.valor !== '' && r.c.valor != null && dinDe(r, 'valor', ctx.moeda), r.c.pago && '✓ ' + t('pago'), r.c.confirmada && !r.c.pago && '✔ ' + t('confirmada')),
  data: 'data', anexos: true,
  // confirmada: o interruptor "Reserva confirmada" ou já paga (reserva paga está confirmada)
  feito: r => confirmada(r),
  filtros: [[N_('Falta confirmar'), r => !confirmada(r)], [N_('Confirmadas'), r => confirmada(r)]], filtroArquivados: true, semEnviarLista: true,
  progresso: l => ({ a: l.filter(confirmada).length, b: l.length, rotulo: N_('{a} de {b} confirmadas') }),
  aoSalvar: (r, ctx) => gastoDaReserva(ctx, r),
  // texto 19 (G2b): a Volta começa VAZIA (só a data de embarque vem da viagem); o fuso vem o do destino
  novo: (_, pai) => ({ data: pai?.c.inicio || '', fim: '', fuso: pai?.c.fuso || '' }),
  agenda: r => ({ time: r.c.hora || null, tz: r.c.hora ? r.c.fuso || null : null, notes: junta(r.c.codigo && t('Código: {c}', { c: r.c.codigo }), r.c.endereco) }),
  resumo: (l, ctx) => (l.length ? t('Total das reservas: {v}', { v: dinSomas(somaPorMoeda(l, r => num(r.c.valor), ctx.moeda), ctx.moeda) }) : null),   // 0.69 (B1): separado por moeda
  vazio: N_('Voos, hotéis, carro e passeios num lugar só, com o código e o comprovante.'),
};

const mala: Colecao = {
  id: 'mala', pai: 'viagem', nome: N_('Mala'), um: N_('Lista da mala'), icone: '🧳',
  campos: [{ k: 'nome', r: N_('Nome da lista'), t: 'texto', ph: N_('Ex.: Mala da Ana') }],
  titulo: r => r.c.nome || '', itens: N_('Itens'),
  feito: r => !!r.itens?.length && r.itens.every(i => i.done),
  filtros: [[N_('Falta pôr'), r => (r.itens ?? []).some(i => !i.done)], [N_('Na mala'), r => !!r.itens?.length && r.itens.every(i => i.done)]], filtroArquivados: true, semEnviarLista: true,
  progresso: l => { const it = l.flatMap(r => r.itens ?? []); return { a: it.filter(i => i.done).length, b: it.length, rotulo: N_('{a} de {b} na mala') }; },
  painel: PainelMala,
};

const TIPOS_LUGAR = [N_('Ponto turístico'), N_('Museu'), N_('Restaurante'), N_('Café ou bar'), N_('Praia'), N_('Parque ou natureza'), N_('Compras'),
  N_('Vida noturna'), N_('Mirante'), N_('Show ou passeio'), N_('Outro')];
// texto 19 (G1d): o 🖼️ do museu parecia um quadradinho de foto vazia: agora cada tipo tem um ícone que se reconhece
const ICONE_LUGAR: Record<string, string> = { 'Ponto turístico': '🏛️', 'Museu': '🏺', 'Restaurante': '🍽️', 'Café ou bar': '☕', 'Praia': '🏖️', 'Parque ou natureza': '🌳',
  'Compras': '🛍️', 'Vida noturna': '🍸', 'Mirante': '🌄', 'Show ou passeio': '🎟️' };
/** Tipo escrito em "Outro": ícone pela palavra (igreja, castelo, parque...). */
const ICONE_PALAVRA: [RegExp, string][] = [[/igreja|catedral|bas[ií]lica|templo|mosteiro|capela/i, '⛪'], [/castelo|pal[aá]cio|forte/i, '🏰'],
  [/parque|jardim|trilha|cachoeira|natureza/i, '🌳'], [/praia|ilha/i, '🏖️'], [/museu|galeria|exposi/i, '🏺'], [/mercado|feira|loja|shopping/i, '🛍️'],
  [/bar|pub|caf[eé]/i, '☕'], [/restaurante|comida|almo|jantar/i, '🍽️'], [/show|teatro|concerto|est[aá]dio|jogo/i, '🎟️'], [/mirante|vista|torre/i, '🌄']];
const iconeLugar = (tipo: string) => ICONE_LUGAR[tipo] ?? ICONE_PALAVRA.find(([re]) => re.test(tipo))?.[1] ?? '📍';
/** Endereço para o mapa: o endereço do lugar ou o nome com a cidade do destino. */
const ondeFica = (r: Reg, destino: string) => (String(r.c.endereco ?? '').trim() || [r.c.nome, destino].filter(Boolean).join(', '));

/** Ideias de lugares no mapa, rota por todos os que faltam e pesquisa por tipo de lugar no destino. */
function PainelLugares({ ctx, pai, lista }: PainelProps) {
  if (!pai) return null;
  const destino = String(pai.c.destino ?? '');
  const faltam = lista.filter(r => !r.c.visitado).sort((a, b) => String(a.c.dia || '9').localeCompare(String(b.c.dia || '9')));
  const busca = (o: string) => abrirBusca(`${t(o)} ${destino}`.trim());
  return (
    <CartaoApoio titulo={'🔎 ' + t('Descobrir lugares em {d}', { d: destino || t('destino') })} chave={'viagens:descobrir'} gap={8}>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        {[N_('Pontos turísticos'), N_('Restaurantes'), N_('Museus'), N_('Cafés'), N_('Parques'), N_('Compras'), N_('Mirantes')].map(o =>
          <Chip key={o} small cor={ctx.cor} on={false} label={t(o)} onPress={() => busca(o)} />)}
      </View>
      {faltam.length > 1 && (
        <Pressable onPress={() => rotaPorTodos(faltam.map(r => ondeFica(r, destino)))} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
          <Text style={{ color: '#fff', fontWeight: '800' }}>🗺️ {t('Rota passando pelos {n} lugares que faltam', { n: Math.min(faltam.length, 10) })}</Text>
        </Pressable>
      )}
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Os lugares abrem no Google Maps, no Waze ou no Mapas, como escolhido em Ajustes.')}</Text>
      <Dica texto={t('Marque o dia e o horário no lugar para ele aparecer sozinho no Roteiro.')} />
    </CartaoApoio>
  );
}

/** 0.59: pôr no roteiro = marcar o dia do lugar (hoje, se a viagem já começou; senão, o primeiro dia). Ele aparece sozinho no Roteiro. */
function porNoRoteiro(ctx: Ctx, r: Reg) {
  const v = ctx.porId(r.pai);
  const dia = r.c.dia || (v && viagemNoDia(v, today()) ? today() : v?.c.inicio || today());
  mudarCampos(ctx.mod, r.id, { dia });
  Alert.alert(t('No roteiro'), t('"{nome}" está no roteiro de {data}. Para mudar o dia ou o horário, edite o lugar.', { nome: r.c.nome || '', data: dataBr(dia) }));
}

const lugar: Colecao = {
  id: 'lugar', pai: 'viagem', nome: N_('Lugares'), um: N_('Lugar para visitar'), icone: r => (r.c.visitado ? '✅' : iconeLugar(String(r.c.tipo ?? ''))),
  campos: [
    { k: 'nome', r: N_('Lugar'), t: 'texto', ph: N_('Ex.: Torre de Belém') },
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS_LUGAR, outro: true },
    { k: 'prioridade', r: N_('Prioridade'), t: 'opcao', op: [N_('Imperdível'), N_('Quero ir'), N_('Se der tempo')] },
    { k: 'endereco', r: N_('Endereço (opcional)'), t: 'endereco' },
    { k: 'dia', r: N_('Dia planejado'), t: 'data', semHoje: true },
    { k: 'hora', r: N_('Horário'), t: 'hora' },
    { k: 'preco', r: N_('Preço ou ingresso'), t: 'dinheiro' },
    { k: 'link', r: N_('Site ou ingresso online'), t: 'link' },
    { k: 'visitado', r: N_('Já visitei'), t: 'sim', interruptor: true, dataAoLigar: 'visitadoEm', rotuloLigado: N_('Visitado em {d}') },
    // texto 19 (G1e): depois de visitar, uma nota opcional (vai para o relatório)
    { k: 'nota', r: N_('Avaliação'), t: 'estrelas', mostrar: c => !!c.visitado },
    { k: 'comoFoi', r: N_('Como foi?'), t: 'texto', ph: N_('Uma frase (opcional)'), mostrar: c => !!c.visitado },
    { k: 'voltaria', r: N_('Voltaria?'), t: 'opcao', op: [N_('Sim'), N_('Não')], mostrar: c => !!c.visitado },
    { k: 'notas', r: N_('Dicas e observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '', sub: (r, ctx) => junta(op(r.c.tipo), op(r.c.prioridade), r.c.dia && dataBr(r.c.dia), horaTela(r.c.hora), num(r.c.preco) ? din(r.c.preco, ctx.moeda) : '', num(r.c.nota) && '★'.repeat(num(r.c.nota))),
  ordem: (a, b) => { const p = (r: Reg) => ['Imperdível', 'Quero ir', 'Se der tempo'].indexOf(r.c.prioridade); return (a.c.visitado ? 1 : 0) - (b.c.visitado ? 1 : 0) || String(a.c.dia || '9').localeCompare(String(b.c.dia || '9')) || p(a) - p(b); },
  feito: r => !!r.c.visitado, check: 'visitado', rapido: 'prioridade', anexos: true,
  novo: () => ({ prioridade: 'Quero ir' }),
  agenda: r => (r.c.dia ? { start: r.c.dia, time: r.c.hora || null } : null),
  acoes: (r, ctx) => {
    const destino = String(ctx.porId(r.pai)?.c.destino ?? '');
    return [
      { label: '🔎 ' + t('Pesquisar no mapa'), onPress: () => abrirBusca(ondeFica(r, destino)) },
      { label: '🗺️ ' + t('Como chegar'), onPress: () => rotaPorTodos([ondeFica(r, destino)]) },
      ...(r.c.dia ? [] : [{ label: '🗓️ ' + t('Pôr no roteiro da viagem'), onPress: () => porNoRoteiro(ctx, r) }]),
    ];
  },
  enviarPara: (r, ctx) => [...(r.c.dia ? [] : [{ label: '🗺️ ' + t('Para o roteiro desta viagem'), onPress: () => porNoRoteiro(ctx, r) }]), ...paraOutraViagem(ctx, r)],
  filtros: [[N_('Faltam'), r => !r.c.visitado], [N_('Imperdíveis'), r => r.c.prioridade === 'Imperdível'], [N_('Visitados'), r => !!r.c.visitado]],
  filtroArquivados: true, semEnviarLista: true,
  progresso: l => ({ a: l.filter(r => !!r.c.visitado).length, b: l.length, rotulo: N_('{a} de {b} visitados') }),
  painel: PainelLugares,
  vazio: N_('Anote os lugares que quer conhecer. Pesquise no Google Maps ou no Waze, trace a rota passando por todos e coloque no roteiro do dia.'),
};

const gasto: Colecao = {
  id: 'gasto', pai: 'viagem', nome: N_('Orçamento e gastos'), um: N_('Gasto'), icone: '💳',
  campos: [
    { k: 'desc', r: N_('Descrição'), t: 'texto' },
    // texto 19 (D7): agora cada gasto tem a moeda dele (sem conversão); este campo antigo só aparece nos gastos que já o usavam
    { k: 'valorDest', r: N_('Valor na moeda do destino'), t: 'numero', mostrar: (c, ctx, pai) => !!ctx && !!cambioDa(pai, ctx) && !!num(c.valorDest) },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'parada', r: N_('Parada (cidade)'), t: 'ref', ref: 'parada', refPai: true, mostrar: (_c, ctx, pai) => !!ctx && !!pai && ctx.filhos(pai.id, 'parada').length > 0 },
    { k: 'cat', r: N_('Categoria'), t: 'opcao', traduzir: true, opDe: (ctx, pai, c) => categoriasDe(ctx, pai, c).filter(x => x !== 'Outro'), outro: true },
    { k: 'item', r: N_('Item da categoria'), t: 'opcao', traduzir: true, opDe: (ctx, pai, c) => itensDaCategoria(ctx, pai, c),
      mostrar: (c, ctx, pai) => !!ctx && itensDaCategoria(ctx, pai ?? null, c).length > 0 },
    { k: 'pagou', r: N_('Quem pagou'), t: 'opcao', opDe: (_c, pai) => pessoasDaViagem(pai), semOpcoes: N_('Coloque as pessoas em "Quem vai" (edite a viagem).') },
    { k: 'entre', r: N_('Dividir entre (nenhum marcado = todos)'), t: 'multi', opDe: (_c, pai) => pessoasDaViagem(pai), semOpcoes: N_('Coloque as pessoas em "Quem vai" (edite a viagem).'),
      mostrar: (_c, _ctx, pai) => pessoasDaViagem(pai ?? null).length > 1 },
    // texto 19 (A1): gasto lançado como "a pagar" (ex.: o passeio que se paga lá); o normal é já pago
    { k: 'pago', r: N_('Já está pago'), t: 'sim', interruptor: true, seg: 'fin' },
  ],
  titulo: r => (r.c.acerto ? '🤝 ' : '') + (r.c.desc || ''),
  sub: (r, ctx) => r.c.acerto ? junta(dinDe(r, 'valor', ctx.moeda), t('acerto entre os viajantes (não entra no orçamento)')) : junta(dinDe(r, 'valor', ctx.moeda) + (num(r.c.valorDest) && cambioDa(ctx.porId(r.pai), ctx) ? ` (${dinheiro(num(r.c.valorDest), cambioDa(ctx.porId(r.pai), ctx)!.moeda)})` : ''), junta(op(r.c.cat), op(r.c.item)).replace(' · ', ' › '), ctx.porId(r.c.parada)?.c.cidade, r.c.pagou && t('pago por {p}', { p: r.c.pagou }), r.c.pago === false && t('a pagar')),
  data: 'data', ordem: 'data-desc', anexos: true, moedaPorValor: true,
  // texto 19 (D7): a moeda padrão do gasto é a do destino
  novo: (ctx, pai) => ({ data: today(), pago: true, pagou: pessoasDaViagem(pai)[0] ?? '', parada: paradaNoDia(ctx, pai?.id ?? null, today())?.id ?? '',
    ...(pai?.c.moedaDestino && pai.c.moedaDestino !== ctx.moeda ? { moedas: { valor: pai.c.moedaDestino } } : {}) }),
  // 0.59: mudou a data: a parada acompanha (a cidade em que estava naquele dia); mudou a categoria: o item é escolhido de novo
  ajustar: (c, mudou, ctx, pai) => {
    // 0.60: valor na moeda do destino vira valor na sua moeda pelo câmbio da viagem
    if (mudou === 'valorDest') {
      const cb = cambioDa(pai, ctx);
      if (cb?.taxa && num(c.valorDest)) return { ...c, valor: Math.round(num(c.valorDest) * cb.taxa * 100) / 100 };
    }
    if (mudou === 'data' && c.data) {
      const atual = ctx.porId(c.parada);
      if (!atual || !cobre(atual, c.data)) { const p = paradaNoDia(ctx, pai?.id ?? atual?.pai ?? null, c.data); return { ...c, parada: p?.id ?? (atual ? '' : c.parada ?? '') }; }
    }
    if (mudou === 'cat') return { ...c, item: '' };
    return c;
  },
  // texto 19 (A1/B3): A pagar · Pagos; "Acertos" só quando a viagem tem 2 pessoas ou mais
  filtros: [[N_('A pagar'), r => !r.c.acerto && r.c.pago === false], [N_('Pagos'), r => !r.c.acerto && r.c.pago !== false]],
  filtrosDe: (l, ctx) => { const v = l[0] ? ctx.porId(l[0].pai) : null; return v && pessoasDaViagem(v).length > 1 ? [[N_('Acertos'), r => !!r.c.acerto]] : []; },
  filtroArquivados: true, semEnviarLista: true,
  progresso: l => { const g = l.filter(r => !r.c.acerto); return { a: g.filter(r => r.c.pago !== false).length, b: g.length, rotulo: N_('{a} de {b} pagos') }; },
  painel: PainelGastos,
};

// ─── Paradas e orçamento (0.59) ──────────────────────────────────────────────────────────────────
const cobre = (p: Reg, dia: string) => !!p.c.chegada && p.c.chegada <= dia && dia <= (p.c.saida || p.c.chegada);
/** A parada em que a pessoa está naquele dia (pelas datas de chegada e saída). */
function paradaNoDia(ctx: Ctx, viagem: string | null, dia: string) {
  return ctx.regs.find(p => p.col === 'parada' && (!viagem || p.pai === viagem) && cobre(p, dia)) ?? null;
}
/** Categorias para o gasto: as do orçamento da viagem e das paradas, sem repetir. */
function categoriasDe(ctx: Ctx, pai: Reg | null, c?: Record<string, any>) {
  const orcs = [orcDe(pai), ...(pai ? ctx.filhos(pai.id, 'parada').map(orcDe) : [])];
  const nomes = [...ORC_PADRAO(), ...orcs.flat()].map(x => x.nome);
  if (c?.cat) nomes.push(c.cat);
  return [...new Map(nomes.map(n => [norm(n), n])).values()];
}
/** Itens da categoria escolhida (ex.: Alimentação › Almoço). */
function itensDaCategoria(ctx: Ctx, pai: Reg | null, c?: Record<string, any>) {
  if (!c?.cat) return [];
  const alvo = norm(String(c.cat));
  const parada = ctx.porId(c.parada);
  const orcs = [...(parada ? [orcDe(parada)] : []), orcDe(pai), ...(pai ? ctx.filhos(pai.id, 'parada').map(orcDe) : []), ORC_PADRAO()];
  const linhas = orcs.flat().filter(x => norm(x.nome) === alvo).flatMap(x => x.linhas ?? []).map(l => l.nome);
  if (c.item) linhas.push(c.item);
  return [...new Map(linhas.map(n => [norm(n), n])).values()];
}

/** Texto 19 (D7): gastos (sem os acertos) somados por moeda, sem converter. */
const somasGastos = (gastos: Reg[], moeda: string): Somas => somaPorMoeda(gastos.filter(g => !g.c.acerto), g => num(g.c.valor), moeda);
/** Os gastos em outras moedas (fora a do orçamento), para aparecerem à parte: "€ 100,00". */
function outrasMoedas(gastos: Reg[], moeda: string) {
  const m = somasGastos(gastos, moeda); m.delete(moeda);
  return [...m.values()].some(v => Math.abs(v) >= 0.005) ? dinSomas(m, moeda) : '';
}

/** Campo de valor da linha do dia: ao confirmar, cria o gasto. */
function ValorRapido({ rotulo, moeda, onValor }: { rotulo: string; moeda: string; onValor: (v: number) => void }) {
  const [txt, setTxt] = useState('');
  const ok = () => { const v = lerValor(txt); if (isFinite(v) && v > 0) { onValor(Math.round(v * 100) / 100); setTxt(''); } };
  return (
    <TextInput value={txt} onChangeText={setTxt} onSubmitEditing={ok} onBlur={ok} keyboardType="decimal-pad" returnKeyType="done" inputAccessoryViewButtonLabel={t('Concluído')}
      placeholder={din(0, moeda)} placeholderTextColor={C.mute} accessibilityLabel={rotulo}
      style={{ width: 104, backgroundColor: C.card2, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7, color: C.text, fontSize: 15, fontWeight: '700', textAlign: 'right' }} />
  );
}

/** Texto 19 (D): "Gastos do dia": as mesmas categorias e itens do orçamento, num dia (◀ ▶ e Hoje). Digitar o valor cria o gasto
 *  daquele dia; tocar no nome abre "Novo gasto" preenchido (ou a lista dos gastos do item, com "+ outro gasto"). */
function GastosDoDia({ ctx, pai, lista, abrir, novo, pode }: PainelProps & { pai: Reg }) {
  const ini = String(pai.c.inicio || ''), fim = String(pai.c.fim || pai.c.inicio || '');
  // texto 26 (D): abre em HOJE se hoje está dentro das datas da viagem; senão, no PRIMEIRO DIA. CAUSA do "abriu em hoje":
  // o dia era escolhido uma vez só, quando o painel aparecia; se as datas da viagem ainda não estavam carregadas (ou mudaram
  // depois), ficava preso em hoje. Agora o dia padrão é recalculado sempre, até a pessoa andar com ◀ ▶ ou tocar em Hoje.
  const [escolhido, setDia] = useState<string | null>(null);
  const dia = escolhido ?? (ini && today() >= ini && today() <= fim ? today() : ini || today());
  const [aberto, setAberto] = useState<string | null>(null);
  const moedaPad = String(pai.c.moedaDestino || ctx.moeda);
  const pessoas = pessoasDaViagem(pai);
  const doDia = lista.filter(g => !g.c.acerto && g.c.data === dia);
  const base = (cat: string, item: string) => ({ desc: t(item || cat), data: dia, cat, item, pago: true, pagou: pessoas[0] ?? '',
    parada: paradaNoDia(ctx, pai.id, dia)?.id ?? '', ...(moedaPad !== ctx.moeda ? { moedas: { valor: moedaPad } } : {}) });
  const lancar = (cat: string, item: string, v: number) => salvarReg(ctx.mod, novoReg('gasto', pai.id, { ...base(cat, item), valor: v }));
  const linha = (c: CatOrc, l: LinhaOrc | null) => {
    const item = l?.nome ?? '';
    const gs = doDia.filter(g => mesmo(g.c.cat, c.nome) && (l ? mesmo(g.c.item, l.nome) : true));
    const k = c.id + '|' + (l?.id ?? '');
    const porDia = num((l ?? c).porDia);
    const noApp = l ? gastoLinha(gs, c, l, ctx.moeda) : gastoCat(gs, c, ctx.moeda);
    return (
      <View key={k} style={{ gap: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingLeft: l ? 26 : 0 }}>
          <Pressable style={{ flex: 1, minWidth: 0 }} hitSlop={4} disabled={!pode && !gs.length}
            onPress={() => (gs.length ? setAberto(a => (a === k ? null : k)) : novo(base(c.nome, item)))}>
            <Text style={{ color: C.text, fontWeight: l ? '400' : '800' }} numberOfLines={1}>{l ? '' : c.icone + ' '}{t(l?.nome ?? c.nome)}{gs.length > 1 ? `  (${gs.length})` : ''}</Text>
            {porDia > 0 && noApp > 0 && <Text style={{ color: noApp > porDia ? C.danger : C.sub, fontSize: 11 }}>{t('{g} de {p}', { g: din(noApp, ctx.moeda), p: din(porDia, ctx.moeda) })}</Text>}
          </Pressable>
          {gs.length
            ? <Pressable onPress={() => setAberto(a => (a === k ? null : k))} hitSlop={6}><Text style={{ color: C.text, fontWeight: '800', textAlign: 'right' }}>{dinSomas(somasGastos(gs, ctx.moeda), ctx.moeda)}</Text></Pressable>
            : pode ? <ValorRapido rotulo={t('Gasto de {nome} em {d}', { nome: t(l?.nome ?? c.nome), d: dataBr(dia) })} moeda={moedaPad} onValor={v => lancar(c.nome, item, v)} /> : null}
        </View>
        {aberto === k && (
          <View style={{ paddingLeft: l ? 40 : 14, gap: 4 }}>
            {gs.map(g => (
              <Pressable key={g.id} onPress={() => abrir(g)} style={{ flexDirection: 'row', gap: 8 }}>
                <Text style={{ color: C.sub, flex: 1, fontSize: 13 }} numberOfLines={1}>{g.c.desc || t('Gasto')}{g.c.pagou ? ' · ' + g.c.pagou : ''}</Text>
                <Text style={{ color: C.sub, fontSize: 13 }}>{dinDe(g, 'valor', ctx.moeda)}</Text>
              </Pressable>))}
            {pode && <Pressable onPress={() => novo(base(c.nome, item))} hitSlop={6}><Text style={{ color: ctx.cor, fontSize: 12, fontWeight: '700' }}>+ {t('outro gasto')}</Text></Pressable>}
          </View>
        )}
      </View>
    );
  };
  const noDia = (d: string) => (ini && d >= ini && d <= fim ? t('Dia {d} de {total}', { d: diasEntre(ini, d) + 1, total: diasEntre(ini, fim) + 1 }) : '');
  // texto 26 (C2): "▸ Gastos do dia · 24/10 · R$ 45,00"; cada categoria também recolhe, mostrando só o total dela
  const totalDia = doDia.length ? dinSomas(somasGastos(doDia, ctx.moeda), ctx.moeda) : din(0, ctx.moeda);
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
      <Recolhivel titulo={'📆 ' + t('Gastos do dia')} resumo={`${dataBr(dia).slice(0, 5)} · ${totalDia}`} chave={`viagens:${pai.id}:gastosDia`} gap={10} estiloBarra={{ paddingVertical: 2 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Pressable onPress={() => setDia(addDays(dia, -1))} hitSlop={10} accessibilityLabel={t('Dia anterior')}><Text style={{ color: ctx.cor, fontSize: 18, fontWeight: '800' }}>◀</Text></Pressable>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>{dataBr(dia)}</Text>
          {!!noDia(dia) && <Text style={{ color: C.sub, fontSize: 11 }}>{noDia(dia)}</Text>}
        </View>
        <Pressable onPress={() => setDia(addDays(dia, 1))} hitSlop={10} accessibilityLabel={t('Próximo dia')}><Text style={{ color: ctx.cor, fontSize: 18, fontWeight: '800' }}>▶</Text></Pressable>
        {dia !== today() && <Chip small cor={ctx.cor} on={false} label={t('Hoje')} onPress={() => setDia(today())} />}
      </View>
      {orcDe(pai).map(c => {
        const daCat = doDia.filter(g => mesmo(g.c.cat, c.nome));
        return (
        <View key={c.id} style={{ backgroundColor: C.card2, borderRadius: 12, padding: 10, gap: 8 }}>
          {c.linhas?.length ? (
            <Recolhivel titulo={`${c.icone} ${t(c.nome)}`} resumo={daCat.length ? dinSomas(somasGastos(daCat, ctx.moeda), ctx.moeda) : din(0, ctx.moeda)}
              chave={`viagens:${pai.id}:gastosDia:${c.id}`} gap={8} estiloBarra={{ paddingVertical: 0 }}>
              {c.linhas.map(l => linha(c, l))}
            </Recolhivel>
          ) : linha(c, null)}
        </View>); })}
      {!!doDia.length && <Text style={{ color: C.sub, fontSize: 12 }}>{t('Total do dia: {v}', { v: dinSomas(somasGastos(doDia, ctx.moeda), ctx.moeda) })}</Text>}
      </Recolhivel>
    </View>
  );
}

/** Aba Gastos: orçamento geral da viagem, o de cada parada e a divisão das despesas. */
function PainelGastos(p: PainelProps) {
  const { ctx, pai, lista, abrir } = p;
  if (!pai) return null;
  const paradas = ctx.filhos(pai.id, 'parada').sort((a, b) => String(a.c.chegada || '9').localeCompare(String(b.c.chegada || '9')));
  const idsParada = new Set(paradas.map(x => x.id));
  const geral = lista.filter(g => !g.c.parada || !idsParada.has(g.c.parada));
  const prevGeral = previstoTotal(orcDe(pai));
  const prevParadas = paradas.reduce((s, x) => s + previstoTotal(orcDe(x)), 0);
  const previsto = prevGeral + prevParadas || num(pai.c.orcamento);
  const pode = p.pode !== false;
  return (
    <View style={{ gap: 10 }}>
      <PainelCambio ctx={ctx} pai={pai} pode={pode} />
      <ResumoOrcamento titulo={t('Orçamento da viagem inteira')} previsto={previsto} gasto={somaGastos(lista, ctx.moeda)} moeda={ctx.moeda} cor={ctx.cor} outras={outrasMoedas(lista, ctx.moeda)} />
      <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
        {/* texto 26 (C3): o app lembra se ficou aberto ou fechado */}
        <Recolhivel titulo={'🧾 ' + t('Orçamento geral da viagem')} chave={`viagens:${pai.id}:orcGeral`} gap={10} estiloBarra={{ paddingVertical: 2 }}
          resumo={t('Previsto {p} · gasto {g}', { p: din(prevGeral, ctx.moeda), g: dinSomas(somasGastos(geral, ctx.moeda), ctx.moeda) })} linhas={2}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{paradas.length
            ? t('Aqui ficam os custos da viagem que não são de uma parada (ex.: passagens, seguro). Cada parada tem o seu orçamento.')
            : t('Coloque quanto pretende gastar em cada categoria. Ao lançar os gastos, o app mostra quanto já foi usado.')}</Text>
          <EditorOrcamento alvo={pai} gastos={geral} ctx={ctx} pode={pode} />
        </Recolhivel>
      </View>
      <GastosDoDia {...p} pai={pai} pode={pode} />
      {paradas.length > 0 && (
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
          <Recolhivel titulo={'🏙️ ' + t('Orçamento por parada')} chave={`viagens:${pai.id}:orcParadas`} gap={8} estiloBarra={{ paddingVertical: 2 }}
            resumo={t('{n} parada(s)', { n: paradas.length })}>
          {paradas.map(x => {
            const gx = lista.filter(g => g.c.parada === x.id);
            return (
              <Pressable key={x.id} onPress={() => abrir(x)} style={{ gap: 4, paddingVertical: 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={{ color: C.text, fontWeight: '700', flex: 1 }} numberOfLines={1}>{x.c.cidade || t('Parada (cidade)')}</Text>
                  <Text style={{ color: C.sub, fontSize: 12 }}>{dinSomas(somasGastos(gx, ctx.moeda), ctx.moeda)} / {din(previstoTotal(orcDe(x)), ctx.moeda)}</Text>
                  <Text style={{ color: C.mute }}>›</Text>
                </View>
              </Pressable>
            );
          })}
          </Recolhivel>
        </View>
      )}
      {!paradas.length && <Dica texto={t('Vai passar por mais de uma cidade? Cadastre as paradas (aba Paradas): cada uma ganha o seu orçamento e os gastos daquelas datas entram nela sozinhos.')} />}
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Chip small cor={ctx.cor} on={false} label={'📄 ' + t('Relatório da viagem (PDF)')} onPress={() => abrirRelatorioViagem(pai.id)} />
        <Chip small cor={ctx.cor} on={false} label={'📊 ' + t('Planilha (Excel)')} onPress={() => { semBloqueio(() => relatorioViagemXlsx(ctx, pai)).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e))); }} />
      </View>
      <PainelAcerto {...p} />
    </View>
  );
}

/** Página da parada: orçamento dela, lançamento rápido de gasto e os gastos lançados. */
function PainelParada({ r, ctx, pode }: TopoProps) {
  const viagem = ctx.porId(r.pai);
  const gastos = ctx.filhos(r.pai!, 'gasto').filter(g => g.c.parada === r.id).sort((a, b) => String(b.c.data).localeCompare(String(a.c.data)));
  const orc = orcDe(r);
  const [desc, setDesc] = useState('');
  const [valor, setValor] = useState('');
  const [cat, setCat] = useState('');
  const [item, setItem] = useState('');
  const cb = cambioDa(viagem, ctx);
  const [naDestino, setNaDestino] = useState(!!cb);
  const linhas = orc.find(x => x.nome === cat)?.linhas ?? [];
  const lancar = () => {
    const v = lerValor(valor);
    if (!isFinite(v) || v <= 0) return Alert.alert(t('Coloque o valor do gasto.'));
    const dia = cobre(r, today()) ? today() : r.c.chegada || today();
    // texto 19 (D7): o gasto fica na moeda escolhida (sem converter)
    const conv = { valor: Math.round(v * 100) / 100, ...(naDestino && cb ? { moedas: { valor: cb.moeda } } : {}) };
    salvarReg(ctx.mod, novoReg('gasto', r.pai, { desc: desc.trim() || t(item || cat || 'Gasto'), ...conv, data: dia, parada: r.id, cat, item,
      pagou: pessoasDaViagem(viagem)[0] ?? '' }));
    setDesc(''); setValor(''); setItem('');
  };
  return (
    <View style={{ gap: 10 }}>
      <ResumoOrcamento titulo={t('Orçamento de {c}', { c: r.c.cidade || t('Parada (cidade)') })} previsto={previstoTotal(orc)} gasto={somaGastos(gastos, ctx.moeda)} moeda={ctx.moeda} cor={ctx.cor} outras={outrasMoedas(gastos, ctx.moeda)} />
      {pode && (
        <CartaoApoio titulo={'➕ ' + t('Lançar um gasto nesta parada')} chave={'viagens:parada:lancar'} gap={8}>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Field value={desc} onChangeText={setDesc} placeholder={t('Descrição')} style={{ flex: 1, minWidth: 0 }} />
            <Field value={valor} onChangeText={setValor} placeholder="0,00" keyboardType="decimal-pad" style={{ width: 110 }} />
          </View>
          {!!cb && (
            <View style={{ flexDirection: 'row', gap: 6 }}>
              <Chip small cor={ctx.cor} on={naDestino} label={t('Valor em {m}', { m: cb.moeda })} onPress={() => setNaDestino(true)} />
              <Chip small cor={ctx.cor} on={!naDestino} label={t('Valor em {m}', { m: ctx.moeda })} onPress={() => setNaDestino(false)} />
            </View>
          )}
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {orc.map(x => <Chip key={x.id} small cor={ctx.cor} on={cat === x.nome} label={`${x.icone} ${t(x.nome)}`} onPress={() => { setCat(cat === x.nome ? '' : x.nome); setItem(''); }} />)}
          </View>
          {!!linhas.length && (
            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
              {linhas.map(l => <Chip key={l.id} small cor={ctx.cor} on={item === l.nome} label={t(l.nome)} onPress={() => setItem(item === l.nome ? '' : l.nome)} />)}
            </View>
          )}
          <Pressable onPress={lancar} style={{ backgroundColor: ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
            <Text style={{ color: '#fff', fontWeight: '800' }}>{t('Lançar gasto')}</Text>
          </Pressable>
        </CartaoApoio>
      )}
      <CartaoApoio titulo={'🧾 ' + t('Orçamento e gastos da parada')} chave={`viagens:${r.id}:orcParada`} gap={10}>
        <EditorOrcamento alvo={r} gastos={gastos} ctx={ctx} pode={pode} />
      </CartaoApoio>
      {!!gastos.length && (
        <CartaoApoio titulo={'💳 ' + t('Gastos lançados ({n})', { n: gastos.length })} chave={`viagens:${r.id}:gastosParada`} resumo={dinSomas(somasGastos(gastos, ctx.moeda), ctx.moeda)} gap={6}>
          {gastos.slice(0, 30).map(g => (
            <View key={g.id} style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
              <Text style={{ color: C.sub, fontSize: 12, width: 44 }}>{dataBr(g.c.data).slice(0, 5)}</Text>
              <Text style={{ color: C.text, flex: 1 }} numberOfLines={1}>{g.c.desc || t('Gasto')}{g.c.cat ? ` · ${t(g.c.cat)}${g.c.item ? ' › ' + t(g.c.item) : ''}` : ''}</Text>
              <Text style={{ color: C.text, fontWeight: '700' }}>{dinDe(g, 'valor', ctx.moeda)}</Text>
            </View>
          ))}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Para corrigir ou apagar um gasto, use a aba Orçamento e gastos da viagem.')}</Text>
        </CartaoApoio>
      )}
    </View>
  );
}

const parada: Colecao = {
  id: 'parada', pai: 'viagem', nome: N_('Paradas'), um: N_('Parada (cidade)'), icone: '🏙️',
  campos: [
    { k: 'cidade', r: N_('Cidade ou lugar'), t: 'texto', ph: N_('Ex.: Porto') },
    { k: 'chegada', r: N_('Chegada'), t: 'data', alerta: true, semHoje: true },
    { k: 'saida', r: N_('Saída'), t: 'data', semHoje: true },
    { k: 'hospedagem', r: N_('Onde vai ficar'), t: 'endereco' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.cidade || '',
  sub: (r, ctx) => {
    const prev = previstoTotal(orcDe(r));
    const gasto = somaGastos(ctx.filhos(r.pai!, 'gasto').filter(g => g.c.parada === r.id), ctx.moeda);
    // texto 19 (G3c): "12/11 → 15/11 · 3 dias"
    const dias = r.c.chegada && r.c.saida && r.c.saida >= r.c.chegada ? diasEntre(r.c.chegada, r.c.saida) : 0;
    return junta(r.c.chegada && `${dataBr(r.c.chegada).slice(0, 5)}${r.c.saida ? ' → ' + dataBr(r.c.saida).slice(0, 5) : ''}`,
      dias ? (dias === 1 ? t('1 dia') : t('{n} dias', { n: dias })) : '',
      (prev || gasto) ? t('gasto {g} de {p}', { g: din(gasto, ctx.moeda), p: din(prev, ctx.moeda) }) : '');
  },
  subCompleto: true,
  vazioTitulo: N_('Nenhuma parada ainda'),
  data: 'chegada', ordem: 'data', anexos: true, pagina: true, segExtra: { orc: 'fin' }, semEnviarLista: true,
  novo: (ctx, pai) => {
    // a próxima parada começa onde a última terminou
    const ult = pai ? ctx.filhos(pai.id, 'parada').map(x => String(x.c.saida || x.c.chegada || '')).filter(Boolean).sort().pop() : '';
    return { chegada: ult || pai?.c.inicio || '' };
  },
  painelTopo: PainelParada,
  vazio: N_('Vai passar por mais de uma cidade? Cadastre cada parada com as datas. Cada uma ganha o seu orçamento (hospedagem, refeições, transporte...) e os gastos daquelas datas entram nela sozinhos.'),
};

/** Pessoas da viagem (texto 19, B1): os membros do grupo da viagem e os nomes avulsos de "Quem vai", sem repetir. */
export function pessoasDaViagem(v: Reg | null): string[] {
  const todos = [...membrosDaViagem(v), ...avulsosDaViagem(v)];
  return [...new Map(todos.map(x => [norm(x), x])).values()].slice(0, 30);
}

/** Divisão das despesas (estilo Splitwise): quanto cada um pagou, quanto devia e quem paga quem no fim. */
export function acertoDeContas(gastos: Reg[], pessoas: string[]) {
  const saldo = new Map<string, number>(pessoas.map(p => [p, 0]));
  const pagoPor = new Map<string, number>(pessoas.map(p => [p, 0]));
  for (const g of gastos) {
    const v = num(g.c.valor);
    if (!v || !g.c.pagou || g.c.pago === false) continue;   // texto 19: gasto "a pagar" ainda não entra no acerto
    const entre: string[] = Array.isArray(g.c.entre) && g.c.entre.length ? g.c.entre : pessoas;
    if (!entre.length) continue;
    saldo.set(g.c.pagou, (saldo.get(g.c.pagou) ?? 0) + v);
    if (!g.c.acerto) pagoPor.set(g.c.pagou, (pagoPor.get(g.c.pagou) ?? 0) + v);   // acerto pago não é despesa da viagem
    for (const p of entre) saldo.set(p, (saldo.get(p) ?? 0) - v / entre.length);
  }
  // menor número de transferências: quem mais deve paga para quem mais tem a receber
  const dev = [...saldo].filter(([, v]) => v < -0.009).map(([p, v]) => ({ p, v: -v })).sort((a, b) => b.v - a.v);
  const rec = [...saldo].filter(([, v]) => v > 0.009).map(([p, v]) => ({ p, v })).sort((a, b) => b.v - a.v);
  const transf: { de: string; para: string; v: number }[] = [];
  let i = 0, j = 0;
  while (i < dev.length && j < rec.length) {
    const x = Math.min(dev[i].v, rec[j].v);
    transf.push({ de: dev[i].p, para: rec[j].p, v: Math.round(x * 100) / 100 });
    dev[i].v -= x; rec[j].v -= x;
    if (dev[i].v < 0.01) i++;
    if (rec[j].v < 0.01) j++;
  }
  return { saldo, pagoPor, transf };
}

/** Texto 19 (D7/B3): acertos separados por moeda (nada é convertido). */
export function acertosPorMoeda(gastos: Reg[], pessoas: string[], moeda: string) {
  const ms = [...new Set(gastos.map(g => moedaDo(g.c, 'valor', moeda)))].sort((a, b) => (a === moeda ? -1 : b === moeda ? 1 : a.localeCompare(b)));
  return ms.map(m => ({ moeda: m, ...acertoDeContas(gastos.filter(g => moedaDo(g.c, 'valor', moeda) === m), pessoas) }));
}

function PainelAcerto({ ctx, pai, lista, pode }: PainelProps) {
  useMembrosViagem();
  const pessoas = pessoasDaViagem(pai);
  // texto 19 (B3/B4): com 1 pessoa não há acerto (nem a dica antiga dos nomes "separados por vírgula")
  if (!pai || pessoas.length < 2 || !lista.some(g => g.c.pagou)) return null;
  const porMoeda = acertosPorMoeda(lista, pessoas, ctx.moeda);
  const transf = porMoeda.flatMap(a => a.transf.map(x => ({ ...x, moeda: a.moeda })));
  const txt = [t('Acerto de contas: {v}', { v: pai.c.destino || '' }), ...transf.map(x => t('{de} deve {v} a {para}', { de: x.de, para: x.para, v: din(x.v, x.moeda) }))].join('\n');
  const somas = (f: (a: ReturnType<typeof acertoDeContas>) => number) => porMoeda.map(a => ({ m: a.moeda, v: f(a) })).filter(x => Math.abs(x.v) >= 0.005);
  return (
    <CartaoApoio titulo={'🤝 ' + t('Acertos')} chave={`viagens:${pai.id}:acertos`} resumo={transf.length ? t('{n} para acertar', { n: transf.length }) : '✅'} gap={6}>
      {pessoas.map(p => {
        const pagou = somas(a => a.pagoPor.get(p) ?? 0), saldo = somas(a => a.saldo.get(p) ?? 0);
        return <Text key={p} style={{ color: C.text, fontSize: 13 }}>{p}: {t('pagou {v}', { v: pagou.length ? pagou.map(x => din(x.v, x.m)).join(' + ') : din(0, ctx.moeda) })}
          {saldo.map(x => <Text key={x.m} style={{ color: x.v >= 0 ? C.ok : C.danger, fontWeight: '700' }}> · {x.v >= 0 ? t('recebe {v}', { v: din(x.v, x.m) }) : t('deve {v}', { v: din(-x.v, x.m) })}</Text>)}</Text>;
      })}
      {transf.length ? (<>
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', marginTop: 4 }}>{t('PARA ACERTAR')}</Text>
        {transf.map((x, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ color: C.text, fontWeight: '600', flex: 1 }}>💸 {t('{de} deve {v} a {para}', { de: x.de, para: x.para, v: din(x.v, x.moeda) })}</Text>
            {pode !== false && <Pressable hitSlop={6} onPress={() => Alert.alert(t('Marcar como acertado?'), t('{de} já pagou {v} para {para}?', { de: x.de, para: x.para, v: din(x.v, x.moeda) }), [
              { text: t('Cancelar'), style: 'cancel' },
              { text: t('Já pagou'), onPress: () => salvarReg(ctx.mod, novoReg('gasto', pai.id, { desc: t('Acerto: {de} pagou {para}', { de: x.de, para: x.para }), valor: x.v, data: today(), pagou: x.de, entre: [x.para], acerto: true,
                ...(x.moeda !== ctx.moeda ? { moedas: { valor: x.moeda } } : {}) })) }])}
              style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: alpha(ctx.cor, 0.18) }}>
              <Text style={{ color: ctx.cor, fontWeight: '800', fontSize: 12 }}>✓ {t('Marcar como acertado')}</Text>
            </Pressable>}
          </View>
        ))}
        <Pressable onPress={() => semBloqueio(() => compartilharTexto(txt, t('Acerto de contas'))).catch(() => {})} style={{ alignSelf: 'flex-start', marginTop: 4 }}>
          <Text style={{ color: ctx.cor, fontWeight: '800' }}>📤 {t('Mandar o acerto para o grupo')}</Text>
        </Pressable>
      </>) : <Text style={{ color: C.ok }}>✅ {t('Ninguém deve nada a ninguém.')}</Text>}
    </CartaoApoio>
  );
}

// Álbum (0.29): fotos e vídeos de todos do grupo num lugar só
const foto: Colecao = {
  id: 'foto', pai: 'viagem', nome: N_('Fotos'), um: N_('Fotos'), icone: '📷',
  campos: [
    { k: 'legenda', r: N_('Legenda'), t: 'texto', ph: N_('Ex.: Pôr do sol no Tejo') },
    { k: 'data', r: N_('Dia'), t: 'data' },
    { k: 'local', r: N_('Onde'), t: 'texto' },
  ],
  titulo: r => r.c.legenda || t('Fotos de {d}', { d: dataBr(r.c.data) || '' }),
  sub: r => junta(r.c.local, t('{n} arquivo(s)', { n: (r.anexos ?? []).length }), r.por && t('por {p}', { p: r.por })),
  ordem: 'data-desc', anexos: true, semEnviarLista: true,
  novo: () => ({ data: today() }),
  painel: PainelAlbum,
  vazio: N_('Ponha aqui as fotos e os vídeos da viagem. No grupo, todos veem e todos podem pôr as suas.'),
};

// ─── Documentos da viagem (0.62) ────────────────────────────────────────────────────────────────
const TIPOS_DOC = [N_('Passaporte'), N_('Visto'), N_('Identidade'), N_('Permissão internacional para dirigir'), N_('Seguro viagem'),
  N_('Vacina (certificado)'), N_('Autorização de viagem de menor'), N_('Outro')];
const ICONE_DOC: Record<string, string> = { 'Passaporte': '🛂', 'Visto': '📑', 'Identidade': '🪪', 'Permissão internacional para dirigir': '🚗',
  'Seguro viagem': '🛡️', 'Vacina (certificado)': '💉', 'Autorização de viagem de menor': '🧒' };
/** Texto 19 (G4d): o app não afirma regra de país. O aviso do passaporte só aparece quando a pessoa preenche
 *  "Validade mínima exigida (meses)" (vem 6 no passaporte novo; apagando, não avisa). "Vence antes da volta" continua. */
function addMeses(ymd: string, n: number) {
  const [y, m, d] = ymd.split('-').map(Number);
  const x = new Date(y, m - 1 + n, 1), ult = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(d, ult));
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}
function alertaDoc(r: Reg, v: Reg | null): string {
  const val = String(r.c.validade || '');
  if (!val) return '';
  const fim = String(v?.c.fim || v?.c.inicio || '');
  if (fim && val < fim) return t('Vence antes da volta');
  const meses = num(r.c.validadeMin);
  if (r.c.tipo === 'Passaporte' && fim && meses > 0 && val < addMeses(fim, meses)) return t('Seu passaporte pode não ser aceito. Confira a exigência do país');
  return '';
}
function PainelDocs({ ctx, pai, lista, pode }: PainelProps) {
  if (!pai) return null;
  const pessoas = pessoasDaViagem(pai);
  const fora = !!cambioDa(pai, ctx);
  const tem = (tipo: string, pessoa: string) => lista.some(d => d.c.tipo === tipo && (d.c.pessoa || '') === pessoa);
  const sugerir = () => {
    const quem = pessoas.length ? pessoas : [''];
    const novos: Reg[] = [];
    for (const p2 of quem) if (fora && !tem('Passaporte', p2)) novos.push(novoReg('docviagem', pai.id, { tipo: 'Passaporte', pessoa: p2, validadeMin: 6 }));
    if (fora && !tem('Seguro viagem', '')) novos.push(novoReg('docviagem', pai.id, { tipo: 'Seguro viagem', pessoa: '' }));
    if (!fora) for (const p2 of quem) if (!tem('Identidade', p2)) novos.push(novoReg('docviagem', pai.id, { tipo: 'Identidade', pessoa: p2 }));
    novos.forEach(x => salvarReg(ctx.mod, x));
    if (!novos.length) Alert.alert(t('A lista básica já está aqui.'));
  };
  const alertas = lista.filter(d => !!alertaDoc(d, pai)).length;
  return (
    <View style={{ gap: 8 }}>
      {alertas > 0 && <Text style={{ color: C.danger, fontWeight: '700' }}>⚠️ {t('{n} documento(s) com validade que pode atrapalhar a viagem', { n: alertas })}</Text>}
      {pode !== false && <Chip small cor={ctx.cor} on={false} label={'＋ ' + (fora ? t('Lista básica: passaporte de cada um e seguro viagem') : t('Lista básica: identidade de cada um'))} onPress={sugerir} />}
      <Dica texto={t('Ponha a validade de cada documento: o app avisa antes de vencer. As fotos dos documentos ficam protegidas no cofre.')} />
    </View>
  );
}

/** Texto 19 (G4c): "Exigências do destino": itens que a PESSOA escreve, com caixinha e data limite opcional (o app não afirma regras). */
function ExigenciasDestino({ ctx, pai, pode }: PainelProps) {
  const [novo, setNovo] = useState('');
  const [ate, setAte] = useState('');
  if (!pai) return null;
  const l: { id: string; texto: string; feito?: boolean; ate?: string }[] = Array.isArray(pai.c.exigencias) ? pai.c.exigencias : [];
  const salvar = (n: typeof l) => mudarCampos(ctx.mod, pai.id, { exigencias: n });
  const ateOk = /^\d{2}\/\d{2}\/\d{4}$/.test(ate.trim());
  const add = () => {
    if (!novo.trim()) return;
    const [d, m, y] = ate.trim().split('/');
    salvar([...l, { id: uid(), texto: novo.trim(), ...(ateOk ? { ate: `${y}-${m}-${d}` } : {}) }]); setNovo(''); setAte('');
  };
  return (
    <CartaoApoio titulo={t('Exigências do destino')} chave={'viagens:exigencias'} resumo={String(l.length)} gap={8}>
      {!l.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Escreva o que o destino pede (ex.: 2 páginas em branco no passaporte, certificado de vacina). Confira no site oficial do país.')}</Text>}
      {l.map(x => (
        <View key={x.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Pressable disabled={pode === false} hitSlop={8} onPress={() => salvar(l.map(y => (y.id === x.id ? { ...y, feito: !y.feito } : y)))} accessibilityRole="checkbox" accessibilityState={{ checked: !!x.feito }}>
            <Text style={{ fontSize: 18, color: x.feito ? ctx.cor : C.sub }}>{x.feito ? '☑' : '☐'}</Text>
          </Pressable>
          <View style={{ flex: 1 }}>
            <Text style={{ color: x.feito ? C.mute : C.text, textDecorationLine: x.feito ? 'line-through' : 'none' }}>{x.texto}</Text>
            {!!x.ate && <Text style={{ color: !x.feito && x.ate < today() ? C.danger : C.sub, fontSize: 11 }}>{t('Até {d}', { d: dataBr(x.ate) })}</Text>}
          </View>
          {pode !== false && <Pressable hitSlop={8} onPress={() => salvar(l.filter(y => y.id !== x.id))} accessibilityLabel={t('Excluir')}><Text style={{ color: C.mute }}>✕</Text></Pressable>}
        </View>
      ))}
      {pode !== false && (
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Field value={novo} onChangeText={setNovo} placeholder={t('Nova exigência')} style={{ flex: 1, minWidth: 0, paddingVertical: 8 }} onSubmitEditing={add} />
          <Field value={ate} onChangeText={x => setAte(x.replace(/[^\d/]/g, '').slice(0, 10))} placeholder={t('Até (dd/mm/aaaa)')} keyboardType="numbers-and-punctuation" style={{ width: 120, paddingVertical: 8 }} />
          <Chip small cor={ctx.cor} on={false} label="+" onPress={add} />
        </View>
      )}
    </CartaoApoio>
  );
}

const docviagem: Colecao = {
  id: 'docviagem', pai: 'viagem', nome: N_('Documentos'), um: N_('Documento'), icone: r => (r.c.pronto ? '✅' : ICONE_DOC[r.c.tipo] ?? '📄'),
  campos: [
    { k: 'tipo', r: N_('Tipo'), t: 'opcao', op: TIPOS_DOC },
    { k: 'pessoa', r: N_('De quem'), t: 'opcao', opDe: (_c, pai) => pessoasDaViagem(pai), semOpcoes: N_('Coloque as pessoas em "Quem vai" (edite a viagem).') },
    { k: 'validade', r: N_('Validade'), t: 'data', aviso: [60, 30, 7], semHoje: true },
    { k: 'validadeMin', r: N_('Validade mínima exigida (meses)'), t: 'numero', mostrar: c => c.tipo === 'Passaporte',
      dica: N_('Opcional. O app avisa se o passaporte vencer antes disso, contado da volta. Confira a exigência do país.') },
    { k: 'numero', r: N_('Número (protegido no cofre)'), t: 'cofre' },
    { k: 'pronto', r: N_('Está em mãos'), t: 'sim', interruptor: true },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => junta(op(r.c.tipo) || t('Documento'), r.c.pessoa),
  // texto 19 (G4b): "vence em 12/2030" e "✔ em mãos"
  sub: (r, ctx) => junta(r.c.validade && t('vence em {d}', { d: dataBr(r.c.validade).slice(3) }), r.c.pronto && '✔ ' + t('em mãos'), alertaDoc(r, ctx.porId(r.pai)) && '⚠️ ' + alertaDoc(r, ctx.porId(r.pai))),
  ordem: (a, b) => (a.c.pronto ? 1 : 0) - (b.c.pronto ? 1 : 0) || String(a.c.tipo).localeCompare(String(b.c.tipo)) || String(a.c.pessoa).localeCompare(String(b.c.pessoa)),
  feito: r => !!r.c.pronto, check: 'pronto', anexos: true,
  filtros: [[N_('Falta'), r => !r.c.pronto], [N_('Em mãos'), r => !!r.c.pronto]], filtroArquivados: true, semEnviarLista: true,
  progresso: l => ({ a: l.filter(r => !!r.c.pronto).length, b: l.length, rotulo: N_('{a} de {b} em mãos') }),
  // texto 19 (F2): pelo ⋮, o vencimento vai para o My Planner (só quando a pessoa manda)
  agenda: (r, ctx) => (r.c.validade ? { start: r.c.validade, title: t('Validade: {d}', { d: docviagem.titulo(r, ctx) }) } : null),
  ajustar: (c, mudou) => (mudou === 'tipo' && c.tipo === 'Passaporte' && (c.validadeMin === '' || c.validadeMin == null) ? { ...c, validadeMin: 6 } : c),
  novo: (ctx, pai) => { const tipo = cambioDa(pai, ctx) ? 'Passaporte' : 'Identidade'; return { tipo, pessoa: pessoasDaViagem(pai)[0] ?? '', ...(tipo === 'Passaporte' ? { validadeMin: 6 } : {}) }; },
  painel: PainelDocs,
  topoLista: ExigenciasDestino,   // texto 19 (G4c): sempre à vista, no topo de Documentos
  vazio: N_('Passaporte, visto, seguro viagem, vacina, autorização de menor... com a validade e a foto. Marque quando estiver em mãos.'),
};

export const VIAGENS: ModDef = { id: 'viagens', colecoes: [viagem, roteiro, foto, lugar, parada, reserva, docviagem, mala, gasto],
  migrar: () => {
    // texto 19 (F1/F4): em Viagens não há mais "enviar a lista inteira": as listas que estavam enviadas saem do My Planner
    // (mesmo critério de Cursos no texto 15 B2). Nada é apagado; o envio passa a ser item por item.
    const aj = getDoc('ajustes');
    const ligadas = VIAGENS.colecoes.filter(c => listaNoPlanner(aj, 'viagens', c.id));
    if (ligadas.length) {
      ligadas.forEach(c => enviarListaAoPlanner('viagens', c.id, false));
      setTimeout(() => Alert.alert(t('Viagens no My Planner'), ligadas.length === 1
        ? t('1 lista de Viagens ia inteira para o My Planner e saiu de lá. Agora o envio é item por item, pelo ⋮ de cada item.')
        : t('{n} listas de Viagens iam inteiras para o My Planner e saíram de lá. Agora o envio é item por item, pelo ⋮ de cada item.', { n: ligadas.length })), 800);
    }
    return [];
  } };

// ─── Relatório da viagem (0.61) ──────────────────────────────────────────────────────────────────
/** Tudo o que o relatório usa: paradas, orçamento por parte, roteiro (com os lugares de dia marcado), reservas e gastos. */
function dadosViagem(ctx: Ctx, v: Reg) {
  const $ = (x: number) => dinheiro(x, ctx.moeda);
  const paradas = ctx.filhos(v.id, 'parada').sort((a, b) => String(a.c.chegada || '9').localeCompare(String(b.c.chegada || '9')));
  const ids = new Set(paradas.map(x => x.id));
  const gastos = ctx.filhos(v.id, 'gasto').sort((a, b) => String(a.c.data).localeCompare(String(b.c.data)));
  const partes = [
    { nome: t('Geral da viagem'), alvo: v, gastos: gastos.filter(g => !g.c.parada || !ids.has(g.c.parada)) },
    ...paradas.map(x => ({ nome: x.c.cidade || t('Parada (cidade)'), alvo: x, gastos: gastos.filter(g => g.c.parada === x.id) })),
  ];
  const lugares = ctx.filhos(v.id, 'lugar').filter(l => !!l.c.dia && !jaNoRoteiro(ctx, l));
  const roteiro = [...ctx.filhos(v.id, 'roteiro'), ...lugares].sort((a, b) => `${diaDe(a)}${a.c.hora || '99'}`.localeCompare(`${diaDe(b)}${b.c.hora || '99'}`));
  const reservas = ctx.filhos(v.id, 'reserva').sort((a, b) => String(a.c.data || '9').localeCompare(String(b.c.data || '9')));
  const previsto = partes.reduce((s2, p2) => s2 + previstoTotal(orcDe(p2.alvo)), 0) || num(v.c.orcamento);
  const gasto = somaGastos(gastos, ctx.moeda);
  const pessoas = pessoasDaViagem(v);
  const acerto = pessoas.length > 1 ? acertoDeContas(gastos.filter(g => moedaDo(g.c, 'valor', ctx.moeda) === ctx.moeda), pessoas) : null;
  return { $, paradas, gastos, partes, roteiro, reservas, previsto, gasto, pessoas, acerto };
}
const nomeParada = (ctx: Ctx, id: unknown) => String(ctx.porId(String(id ?? ''))?.c.cidade ?? '');
const tituloViagem = (v: Reg) => t('Viagem {d}', { d: v.c.destino || '' }).trim();

// ─── Relatório completo (texto 19, E) ─────────────────────────────────────────────────────────────
// Antes de gerar, a pessoa marca as partes. Números de documentos e dados do cofre NUNCA saem (só o tipo e a validade).
// Dinheiro sempre separado por moeda (sem converter).
const PARTES_REL = [['resumo', N_('Resumo da viagem (datas, destino, pessoas)')], ['roteiro', N_('Roteiro por dia (feito e falta)')],
  ['lugares', N_('Lugares (visitados, faltam, imperdíveis e a nota)')], ['reservas', N_('Reservas')], ['docs', N_('Documentos (só o tipo e a validade)')],
  ['mala', N_('Mala (o que foi e o que faltou)')], ['orcamento', N_('Orçamento previsto × gasto por categoria')], ['dias', N_('Gastos por dia')],
  ['acertos', N_('Acertos')], ['notas', N_('Anotações')], ['fotos', N_('Fotos (escolha quais entram)')]] as const;
type ParteRel = typeof PARTES_REL[number][0];
/** "24/10/2026 · Dia 2 de 7" (no relatório, a data de verdade, não "hoje"/"amanhã"). */
function rotuloDiaRel(v: Reg, d: string) {
  const ini = String(v.c.inicio || ''), fim = String(v.c.fim || v.c.inicio || '');
  return junta(dataBr(d), ini && d >= ini && d <= fim ? t('Dia {d} de {total}', { d: diasEntre(ini, d) + 1, total: diasEntre(ini, fim) + 1 }) : '');
}
const PADRAO_REL: ParteRel[] = ['resumo', 'roteiro', 'lugares', 'reservas', 'docs', 'mala', 'orcamento', 'dias', 'acertos', 'notas'];

type FotoRel = { k: string; a: Anexo; legenda: string };
/** As fotos (imagens) do álbum da viagem, na ordem do álbum. */
function fotosDaViagem(ctx: Ctx, v: Reg): FotoRel[] {
  return ctx.filhos(v.id, 'foto').flatMap(f => (f.anexos ?? []).map((a, i) => ({ k: `${f.id}:${i}`, a, legenda: junta(dataBr(f.c.data), f.c.legenda, f.c.local) })))
    .filter(x => ehFoto(x.a));
}

async function relatorioViagemPdf(ctx: Ctx, v: Reg, partesSel: ParteRel[] = PADRAO_REL, fotosSel: FotoRel[] = []) {
  const x = dadosViagem(ctx, v);
  const tem = (p: ParteRel) => partesSel.includes(p);
  const cb = cambioDa(v, ctx);
  const $$ = (l: Reg[]) => dinSomas(somasGastos(l, ctx.moeda), ctx.moeda);
  const vm = (r: Reg, k = 'valor') => dinDe(r, k, ctx.moeda);
  const b: BlocoPdf[] = [{ t: 'titulo', texto: tituloViagem(v) }];
  if (tem('resumo')) {
    b.push({ t: 'par', texto: junta(v.c.destino, v.c.inicio && `${dataBr(v.c.inicio)}${v.c.fim ? ' a ' + dataBr(v.c.fim) : ''}`, cb?.taxa ? `${dinheiro(1, cb.moeda)} = ${x.$(cb.taxa)}` : '') });
    if (x.pessoas.length) b.push({ t: 'par', texto: t('Quem vai: {p}', { p: x.pessoas.join(', ') }) });
    const outras = outrasMoedas(x.gastos, ctx.moeda);
    b.push({ t: 'tabela', colunas: [t('Previsto'), t('Gasto'), x.previsto >= x.gasto ? t('Ainda pode gastar') : t('Passou do previsto')],
      linhas: [[x.$(x.previsto), x.$(x.gasto), x.$(Math.abs(x.previsto - x.gasto))]] });
    if (outras) b.push({ t: 'par', texto: t('Gastos em outras moedas (não convertidos): {v}', { v: outras }) });
  }
  if (tem('roteiro') && x.roteiro.length) {
    b.push({ t: 'sub', texto: t('Roteiro') });
    const dias = [...new Set(x.roteiro.map(diaDe))];
    for (const d of dias) {
      const its = x.roteiro.filter(r => diaDe(r) === d);
      b.push({ t: 'par', negrito: true, texto: d === '9999' ? t('Sem dia marcado') : rotuloDiaRel(v, d) },
        { t: 'tabela', colunas: [t('Horário'), t('Atividade'), t('Local'), t('Situação')], larguras: [0.12, 0.42, 0.3, 0.16],
          linhas: its.map(r => [horaTela(r.c.hora), r.c.atividade || r.c.nome || '', r.c.local || r.c.endereco || '', r.c.feito || r.c.visitado ? t('Feito') : t('Falta')]) });
    }
  }
  const lugares = ctx.filhos(v.id, 'lugar');
  if (tem('lugares') && lugares.length) b.push({ t: 'sub', texto: t('Lugares') },
    { t: 'tabela', colunas: [t('Lugar'), t('Tipo'), t('Prioridade'), t('Situação'), t('Nota'), t('Como foi?')], larguras: [0.24, 0.14, 0.13, 0.19, 0.08, 0.22],
      linhas: lugares.sort((a, c) => Number(!!a.c.visitado) - Number(!!c.c.visitado)).map(r => [r.c.nome || '', op(r.c.tipo), op(r.c.prioridade),
        r.c.visitado ? (r.c.visitadoEm ? t('Visitado em {d}', { d: dataBr(r.c.visitadoEm) }) : t('Visitado')) : t('Falta'),
        num(r.c.nota) ? `${num(r.c.nota)}/5` : '', junta(r.c.comoFoi, r.c.voltaria && t('Voltaria: {r}', { r: t(r.c.voltaria) }))]) });
  if (tem('reservas') && x.reservas.length) b.push({ t: 'sub', texto: t('Reservas') },
    { t: 'tabela', colunas: [t('Data'), t('Tipo'), t('Empresa, hotel ou voo'), t('Código'), t('Valor'), t('Situação')], larguras: [0.12, 0.13, 0.31, 0.13, 0.15, 0.16],
      linhas: x.reservas.map(r => [dataBr(r.c.data), op(r.c.tipo), junta(r.c.nome, r.c.tipo === 'Voo' && (r.c.origem || r.c.destinoVoo) ? `${r.c.origem || '?'} → ${r.c.destinoVoo || '?'}` : ''),
        r.c.codigo || '', num(r.c.valor) ? vm(r) : '', r.c.pago ? t('Paga') : r.c.confirmada ? t('Confirmada') : t('Falta confirmar')]) });
  const docs = ctx.filhos(v.id, 'docviagem');
  // E3: só o tipo, de quem, a validade e se está em mãos (nunca o número nem nada do cofre)
  if (tem('docs') && docs.length) b.push({ t: 'sub', texto: t('Documentos') },
    { t: 'tabela', colunas: [t('Documento'), t('De quem'), t('Validade'), t('Em mãos')], larguras: [0.34, 0.26, 0.2, 0.2],
      linhas: docs.map(r => [op(r.c.tipo), r.c.pessoa || '', dataBr(r.c.validade), r.c.pronto ? t('Sim') : t('Não')]) });
  const malas = ctx.filhos(v.id, 'mala');
  if (tem('mala') && malas.some(m => m.itens?.length)) {
    b.push({ t: 'sub', texto: t('Mala') });
    for (const m of malas.filter(y => y.itens?.length)) {
      const foi = m.itens!.filter(i => i.done).map(i => i.text), faltou = m.itens!.filter(i => !i.done).map(i => i.text);
      b.push({ t: 'par', negrito: true, texto: m.c.nome || t('Lista da mala') });
      if (foi.length) b.push({ t: 'par', texto: t('Foi ({n}): {l}', { n: foi.length, l: foi.join(', ') }) });
      if (faltou.length) b.push({ t: 'par', texto: t('Faltou ({n}): {l}', { n: faltou.length, l: faltou.join(', ') }) });
    }
  }
  if (tem('orcamento')) for (const p2 of x.partes) {
    const orc = orcDe(p2.alvo);
    const linhas = orc.filter(c => previstoCat(c) || gastoCat(p2.gastos, c, ctx.moeda)).map(c => [t(c.nome), x.$(previstoCat(c)), x.$(gastoCat(p2.gastos, c, ctx.moeda)), x.$(previstoCat(c) - gastoCat(p2.gastos, c, ctx.moeda))]);
    const soApp = somaGastos(p2.gastos, ctx.moeda);
    const outros = soApp - orc.reduce((s2, c) => s2 + gastoCat(p2.gastos, c, ctx.moeda), 0);
    if (outros > 0.005) linhas.push([t('Outros gastos'), x.$(0), x.$(outros), x.$(-outros)]);
    if (!linhas.length) continue;
    linhas.push([t('Total'), x.$(previstoTotal(orc)), x.$(soApp), x.$(previstoTotal(orc) - soApp)]);
    b.push({ t: 'sub', texto: t('Orçamento · {n}', { n: p2.nome }) },
      { t: 'tabela', colunas: [t('Categoria'), t('Previsto'), t('Gasto'), t('Diferença')], larguras: [0.4, 0.2, 0.2, 0.2], linhas });
    const outrasP = outrasMoedas(p2.gastos, ctx.moeda);
    if (outrasP) b.push({ t: 'par', texto: t('Gastos em outras moedas (não convertidos): {v}', { v: outrasP }) });
  }
  const gastos = x.gastos.filter(g => !g.c.acerto);
  if (tem('dias') && gastos.length) {
    b.push({ t: 'sub', texto: t('Gastos por dia') });
    for (const d of [...new Set(gastos.map(g => String(g.c.data || '')))].sort()) {
      const gs = gastos.filter(g => String(g.c.data || '') === d);
      b.push({ t: 'par', negrito: true, texto: `${d ? rotuloDiaRel(v, d) : t('Sem data')} · ${$$(gs)}` },
        { t: 'tabela', colunas: [t('Descrição'), t('Categoria'), t('Pagou'), t('Valor')], larguras: [0.4, 0.26, 0.14, 0.2],
          linhas: gs.map(g => [junta(g.c.desc, g.c.pago === false && t('a pagar')), junta(op(g.c.cat), op(g.c.item)), g.c.pagou || '', vm(g)]) });
    }
  }
  if (tem('acertos') && x.pessoas.length > 1) {
    const tr = acertosPorMoeda(x.gastos, x.pessoas, ctx.moeda).flatMap(a => a.transf.map(z => ({ ...z, moeda: a.moeda })));
    if (tr.length) b.push({ t: 'sub', texto: t('Acertos') }, ...tr.map(z => ({ t: 'par' as const, texto: t('{de} deve {v} a {para}', { de: z.de, para: z.para, v: din(z.v, z.moeda) }) })));
  }
  if (tem('notas') && String(v.c.notas ?? '').trim()) b.push({ t: 'sub', texto: t('Anotações') }, { t: 'par', texto: String(v.c.notas).trim() });
  // texto 28 (C2): as fotos entram como IMAGENS (reduzidas), só as que a pessoa marcou
  if (tem('fotos') && fotosSel.length) {
    b.push({ t: 'sub', texto: t('Fotos') });
    let faltou = 0;
    for (const f of fotosSel) {
      const bytes = await fotoReduzida(f.a);
      if (bytes) b.push({ t: 'imagem', bytes, legenda: f.legenda || undefined }); else faltou++;
    }
    if (faltou) b.push({ t: 'par', texto: faltou === 1 ? t('1 foto não pôde ser lida neste dispositivo e ficou de fora.') : t('{n} fotos não puderam ser lidas neste dispositivo e ficaram de fora.', { n: faltou }) });
  }
  marcarAbaAtual('viagens');   // o PDF entra no histórico de relatórios da aba
  await gerarECompartilharPdf(tituloViagem(v), b, 'My Planner');
}

// Janela do relatório (uma só, aberta pelo botão de Orçamento e gastos ou pelo ⋮ da viagem)
let relAberto: string | null = null;
const ouvRel = new Set<() => void>();
export function abrirRelatorioViagem(id: string | null) { relAberto = id; ouvRel.forEach(f => f()); }
const useRelAberto = () => useSyncExternalStore(cb => { ouvRel.add(cb); return () => { ouvRel.delete(cb); }; }, () => relAberto, () => relAberto);
function RelatorioViagem({ ctx }: { ctx: Ctx }) {
  const id = useRelAberto();
  const v = id ? ctx.porId(id) : null;
  const [sel, setSel] = useState<ParteRel[]>(PADRAO_REL);
  const [gerando, setGerando] = useState(false);
  const [fotosMarc, setFotosMarc] = useState<Set<string>>(new Set());
  const todasFotos = v ? fotosDaViagem(ctx, v) : [];
  const fechar = () => abrirRelatorioViagem(null);
  const alternarParte = (k: ParteRel, on: boolean) => {
    setSel(l => (on ? l.filter(y => y !== k) : [...l, k]));
    // C2: ao ligar "Fotos", o app já sugere até 12 (dá para trocar)
    if (k === 'fotos' && !on && !fotosMarc.size) setFotosMarc(new Set(sugerirFotos(todasFotos).map(x => x.k)));
  };
  const gerar = async () => {
    if (!v || gerando) return;
    setGerando(true);
    try { await semBloqueio(() => relatorioViagemPdf(ctx, v, sel, todasFotos.filter(x => fotosMarc.has(x.k)))); }
    catch (e: any) { Alert.alert(t('Não foi possível gerar o PDF.'), String(e?.message ?? e)); }
    finally { setGerando(false); }
  };
  return (
    <Sheet visible={!!v} onClose={fechar} title={t('Relatório da viagem')} larga>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        {!!v && <CartaoTotal valor={dinSomas(somasGastos(ctx.filhos(v.id, 'gasto'), ctx.moeda), ctx.moeda)} legenda={t('gasto na viagem {d}', { d: v.c.destino || '' })} />}
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('O QUE INCLUIR')}</Text>
        {PARTES_REL.map(([k, nome]) => {
          const on = sel.includes(k);
          return (
            <Pressable key={k} onPress={() => alternarParte(k, on)} accessibilityRole="checkbox" accessibilityState={{ checked: on }}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 6 }}>
              <Text style={{ fontSize: 20, color: on ? ctx.cor : C.sub }}>{on ? '☑' : '☐'}</Text>
              <Text style={{ color: C.text, flex: 1 }}>{t(nome)}</Text>
            </Pressable>);
        })}
        {sel.includes('fotos') && (
          <View style={{ gap: 8 }}>
            {!todasFotos.length ? <Text style={{ color: C.sub, fontSize: 12 }}>{t('Nenhuma foto no álbum desta viagem.')}</Text> : (<>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Toque nas fotos para marcar ou desmarcar. Elas entram reduzidas, para o PDF ficar leve.')}</Text>
              <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                <Chip small cor={ctx.cor} on={false} label={t('Sugerir até 12')} onPress={() => setFotosMarc(new Set(sugerirFotos(todasFotos).map(x => x.k)))} />
                <Chip small cor={ctx.cor} on={false} label={t('Nenhuma')} onPress={() => setFotosMarc(new Set())} />
              </View>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {todasFotos.map(f => {
                  const on = fotosMarc.has(f.k), src = fonteImagem(f.a);
                  return (
                    <Pressable key={f.k} onPress={() => setFotosMarc(s0 => { const n = new Set(s0); if (on) n.delete(f.k); else n.add(f.k); return n; })}
                      accessibilityRole="checkbox" accessibilityState={{ checked: on }} accessibilityLabel={f.legenda || t('Foto')}
                      style={{ width: 70, height: 70, borderRadius: 10, overflow: 'hidden', borderWidth: 3, borderColor: on ? ctx.cor : 'transparent', backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}>
                      {src ? <Image source={{ uri: src }} style={{ width: '100%', height: '100%' }} /> : <Text style={{ fontSize: 22 }}>📷</Text>}
                      {on && <View style={{ position: 'absolute', right: 3, top: 3, width: 20, height: 20, borderRadius: 10, backgroundColor: ctx.cor, alignItems: 'center', justifyContent: 'center' }}>
                        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>✓</Text></View>}
                    </Pressable>);
                })}
              </View>
              <Text style={{ color: C.mute, fontSize: 11 }}>{fotosMarc.size === 1 ? t('1 foto marcada') : t('{n} fotos marcadas', { n: fotosMarc.size })}{fotosMarc.size > 12 ? ' · ' + t('Com mais de 12 fotos o PDF fica mais pesado.') : ''}</Text>
            </>)}
          </View>
        )}
        <Btn cor={ctx.cor} outline disabled={gerando || !sel.length} label={'📄 ' + (gerando ? t('Gerando…') : t('Exportar em PDF'))} onPress={gerar} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('Números de documentos e o que está no cofre nunca vão para o relatório. Valores em moedas diferentes aparecem separados.')}</Text>
        <RelatoriosGerados aba="viagens" cor={ctx.cor} />
      </ScrollView>
    </Sheet>
  );
}

async function relatorioViagemXlsx(ctx: Ctx, v: Reg) {
  const x = dadosViagem(ctx, v);
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const orcLinhas: (string | number)[][] = [];
  for (const p2 of x.partes) for (const c of orcDe(p2.alvo)) {
    const prev = previstoCat(c), g = gastoCat(p2.gastos, c);
    if (prev || g) orcLinhas.push([p2.nome, t(c.nome), r2(prev), r2(g), r2(prev - g)]);
  }
  await compartilharXlsx(tituloViagem(v), [
    { nome: t('Orçamento'), colunas: [t('Parte'), t('Categoria'), t('Previsto'), t('Gasto'), t('Diferença')],
      linhas: [...orcLinhas, [t('Total'), '', r2(x.previsto), r2(x.gasto), r2(x.previsto - x.gasto)]] },
    { nome: t('Gastos'), colunas: [t('Data'), t('Descrição'), t('Categoria'), t('Item da categoria'), t('Parada (cidade)'), t('Quem pagou'), t('Valor'), t('Valor na moeda do destino')],
      linhas: x.gastos.map(g => [g.c.data || '', g.c.desc || '', op(g.c.cat), op(g.c.item), nomeParada(ctx, g.c.parada), g.c.pagou || '', r2(num(g.c.valor)), num(g.c.valorDest) ? r2(num(g.c.valorDest)) : '']) },
    { nome: t('Roteiro'), colunas: [t('Dia'), t('Horário'), t('Atividade'), t('Local'), t('Concluído')],
      linhas: x.roteiro.map(r => [r.c.data || r.c.dia || '', r.c.hora || '', r.c.atividade || r.c.nome || '', r.c.local || r.c.endereco || '', r.c.feito || r.c.visitado ? t('Sim') : '']) },
    { nome: t('Reservas'), colunas: [t('Data'), t('Tipo'), t('Empresa, hotel ou voo'), t('Código'), t('Valor'), t('Pago')],
      linhas: x.reservas.map(r => [r.c.data || '', op(r.c.tipo), r.c.nome || '', r.c.codigo || '', num(r.c.valor) ? r2(num(r.c.valor)) : '', r.c.pago ? t('Sim') : t('Não')]) },
  ]);
}
